import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  ArrowLeft,
  Save,
  User,
  Phone,
  PhoneOff,
  MapPin,
  Briefcase,
  Wallet,
  CreditCard,
  Settings,
  Plus,
  Trash2,
  Upload,
  Loader2,
  Search,
  MessageCircle,
  MessageSquare,
  Mail,
  Star,
  Pencil,
  Check,
  CheckCircle,
  FileUp,
  FileText,
  X,
  Building2,
  DollarSign,
  Camera,
  Download,
  FileImage,
  Eye,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
  AlertCircle,
  Edit,
  Ban,
  Send,
  ShieldCheck,
  FilePlus2,
  Clock
,
  ChevronsUpDown,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

// ---- Instituicao Combobox: busca por nome ou codigo ----
function InstituicaoCombobox({ value, onChange, options, testid, placeholder = "Selecione a instituição" }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={testid}
        >
          <span className="truncate text-left">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start" style={{ width: "var(--radix-popover-trigger-width)" }}>
        <Command shouldFilter={true}>
          <CommandInput placeholder="Buscar nome ou código..." data-testid={`${testid}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty>Nenhuma instituição encontrada.</CommandEmpty>
            <CommandGroup>
              {(options || []).map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={() => { onChange(opt); setOpen(false); }}
                  data-testid={`${testid}-option-${opt.split(" - ")[0]}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === opt ? "opacity-100" : "opacity-0"}`} />
                  <span className="text-sm">{opt}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


// Form schema
const formSchema = z.object({
  // Dados Pessoais
  cpf: z.string().min(11, "CPF inválido").max(14),
  nome: z.string().min(3, "Nome deve ter pelo menos 3 caracteres"),
  data_nascimento: z.string().optional(),
  naturalidade: z.string().optional(),
  rg: z.string().optional(),
  orgao_expedidor: z.string().optional(),
  data_emissao_rg: z.string().optional(),
  sexo: z.string().optional(),
  estado_civil: z.string().optional(),
  apelido: z.string().optional(),
  nacionalidade: z.string().optional(),
  nome_pai: z.string().optional(),
  nome_mae: z.string().optional(),
  nome_conjuge: z.string().optional(),
  assina: z.string().optional(),
  usuario_portal: z.string().optional(),
  senha_portal: z.string().optional(),
  dupla_confirmacao_portal: z.boolean().default(false),
  origem_dados: z.string().optional(),
  canal_prospeccao: z.string().optional(),
  primeira_instituicao: z.string().optional(),
  segunda_instituicao: z.string().optional(),
  classificacao: z.string().optional(),
  esteira_atual: z.string().optional(),
});

const sexoOptions = [
  { value: "MASCULINO", label: "Masculino" },
  { value: "FEMININO", label: "Feminino" },
  { value: "OUTRO", label: "Outro" },
];

const estadoCivilOptions = [
  { value: "SOLTEIRO(A)", label: "Solteiro(a)" },
  { value: "CASADO(A)", label: "Casado(a)" },
  { value: "DIVORCIADO(A)", label: "Divorciado(a)" },
  { value: "VIÚVO(A)", label: "Viúvo(a)" },
  { value: "UNIÃO ESTÁVEL", label: "União Estável" },
];

const nacionalidadeOptions = [
  { value: "BRASILEIRO(A)", label: "Brasileiro(a)" },
  { value: "ESTRANGEIRO(A)", label: "Estrangeiro(a)" },
  { value: "NATURALIZADO(A)", label: "Naturalizado(a)" },
];

const assinaOptions = [
  { value: "ASSINA", label: "Assina" },
  { value: "NÃO ASSINA", label: "Não Assina" },
  { value: "A ROGO", label: "A Rogo" },
];

const classificacaoOptions = [
  { value: "INTERNO", label: "Interno (Cliente Ativo)" },
  { value: "EXTERNO", label: "Externo (Novo Lead)" },
];

const esteiraOptions = [
  { value: "CAPTACAO", label: "Captação" },
  { value: "EM_SIMULACAO", label: "Em SIMULAÇÃO" },
  { value: "SEM_INTERESSE", label: "Sem Interesse" },
  { value: "MARGEM_NEGATIVA", label: "Margem Negativa" },
  { value: "COM_VALORES", label: "Com Valores" },
  { value: "SEM_VALORES", label: "Sem Valores" },
  { value: "PROPOSTA_EM_ANALISE", label: "Proposta Em Análise" },
  { value: "PROPOSTA_APROVADO", label: "Proposta Aprovado" },
  { value: "PROPOSTA_REPROVADO", label: "Proposta Reprovado" },
  { value: "FINALIZADO", label: "Finalizado" },
];

const tipoTelefoneOptions = [
  { value: "MÓVEL", label: "Móvel" },
  { value: "FIXO", label: "Fixo" },
  { value: "COMERCIAL", label: "Comercial" },
  { value: "RECADO", label: "Recado" },
];

const tipoContaOptions = [
  { value: "CORRENTE", label: "Corrente" },
  { value: "POUPANÇA", label: "Poupança" },
  { value: "SALÁRIO", label: "Salário" },
  { value: "NÃO INFORMADO", label: "Não Informado" },
];

const meioRecebimentoOptions = [
  { value: "APOSENTADORIA", label: "Aposentadoria" },
  { value: "PENSIONISTA", label: "Pensionista" },
  { value: "SALÁRIO", label: "Salário" },
  { value: "NÃO INFORMADO", label: "Não Informado" },
];

const situacaoFuncionalOptions = [
  "ATIVO PERMANENTE",
  "CELETISTA EMPREGADO",
  "CEDIDO SUS LEI 8270",
  "RESERVA CBM PM",
  "APOSENTADO",
  "REFORMA CBM PM",
  "EMPREGO PCC EX TERRI",
  "CLT ANS DEC 6657 08",
  "ATIVO EM OUTRO ORGAO",
  "NOMEADO CARGO COMIS",
  "EXCEDENTE A LOTACAO",
  "ANIST PRIVADO L10559",
  "EXERC DESCENT CARREI",
  "EXERC 7 ART93 8112",
  "CONTRATO TEMPORARIO",
  "QE MRE CEDIDO",
  "ANIST PUBLICO L10559",
  "CEDIDO REQUISITADO",
  "EMPREGO PUBLICO",
  "CLT APOS COMPLEMENTO",
  "CLT APS DEC JUDICIAL",
  "CONT PROF SUBSTITUTO",
  "REQ DE OUTROS ORGAOS",
  "ATIVO DEC JUDIC",
  "SEM VINCULO",
  "COLAB PCCTAE E MAGIS",
  "CELETISTA DEC JUDIC",
  "CONTR PROF VISITANTE",
  "CLT ANS DEC JUDICIAL",
  "CONTR TEMPORARIO CLT",
  "CELETISTA",
  "QUADRO ESPEC QE MRE",
  "EXERCICIO PROVISORIO",
  "CONT PROF TEMPORARIO",
  "APOSENTADO TCU733 94",
  "CLT ANS JUD CEDIDO",
];


const regimentoOptions = [
  { value: "APOSENTADORIA", label: "Aposentadoria" },
  { value: "PENSIONISTA", label: "Pensionista" },
  { value: "CONCURSADO", label: "Concursado" },
  { value: "CLT", label: "CLT" },
  { value: "COMISSIONADO", label: "Comissionado" },
  { value: "TEMPORÁRIO", label: "Temporário" },
  { value: "AUTÔNOMO", label: "Autônomo" },
];

const tipoResidenciaOptions = [
  { value: "PRÓPRIA", label: "Própria" },
  { value: "ALUGADA", label: "Alugada" },
  { value: "FINANCIADA", label: "Financiada" },
  { value: "CEDIDA", label: "Cedida" },
  { value: "NÃO INFORMADO", label: "Não Informado" },
];

const statusOptions = [
  { value: "ATIVO", label: "Ativo" },
  { value: "INATIVO", label: "Inativo" },
];

const tipoSalarioOptions = [
  { value: "SALÁRIO MÍNIMO", label: "Salário Mínimo" },
  { value: "ACIMA DO MÍNIMO", label: "Acima do Mínimo" },
];

export default function ClienteForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const [buscandoNV, setBuscandoNV] = useState(false);
  const buscarNovaVida = async () => {
    if (!id) return;
    setBuscandoNV(true);
    try {
      const resp = await axios.post(`${API}/clientes/${id}/nova-vida`);
      if (resp.data?.bloqueado) {
        toast.info("Cliente já consultado na Nova Vida nos últimos 12 meses (bloqueado para economia de crédito).");
      } else if (resp.data?.ok) {
        const n = (resp.data.campos_atualizados || []).length;
        toast.success(`Dados Nova Vida atualizados (${n} campo(s)). Recarregando...`);
        setTimeout(() => window.location.reload(), 900);
      } else {
        toast.error("Não foi possível atualizar pela Nova Vida.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao consultar Nova Vida");
    } finally { setBuscandoNV(false); }
  };
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("principal");
  const [fotoPreview, setFotoPreview] = useState(null);
  const [clienteInterno, setClienteInterno] = useState(false);
  const [lookups, setLookups] = useState({
    bancos: [],
    instituicoes: [],
    estados: [],
    origens: [],
    canaisProspeccao: [],
    especiesAposentadoria: [],
    especiesPensao: [],
    especiesAuxilio: [],
    todasEspecies: []
  });
  const [lotacoesDisponiveis, setLotacoesDisponiveis] = useState([]);
  const [temLotacoes, setTemLotacoes] = useState(false);
  
  // Additional form state not in main form
  const [telefones, setTelefones] = useState([]);
  const [emails, setEmails] = useState([]);
  const [telefonesExcluidos, setTelefonesExcluidos] = useState([]);
  const [emailsExcluidos, setEmailsExcluidos] = useState([]);
  const [confirmExclusao, setConfirmExclusao] = useState({ open: false, index: null, numero: "", motivo: "" });
  const [whatsappStatus, setWhatsappStatus] = useState({});
  const [verificandoWhatsapp, setVerificandoWhatsapp] = useState(false);

  // Verificar WhatsApp de todos os telefones
  const verificarWhatsappTodos = async (tels) => {
    const telsList = tels || telefones;
    const numeros = telsList.filter(t => t?.numero).map(t => t.numero.replace(/\D/g, ""));
    if (numeros.length === 0) return;
    setVerificandoWhatsapp(true);
    try {
      const res = await axios.post(`${API}/wpp/verificar-whatsapp`, { numeros });
      setWhatsappStatus(res.data);
      // Auto-preencher LID WhatsApp nos telefones
      const newTelefones = [...telsList];
      let changed = false;
      for (let i = 0; i < newTelefones.length; i++) {
        const numLimpo = newTelefones[i]?.numero?.replace(/\D/g, "") || "";
        const info = res.data[numLimpo];
        if (info?.jid && !newTelefones[i].tel_whatsapp) {
          newTelefones[i].tel_whatsapp = info.jid.replace("@s.whatsapp.net", "");
          changed = true;
        }
        if (info?.lid && !newTelefones[i].lid_whatsapp) {
          newTelefones[i].lid_whatsapp = info.lid;
          changed = true;
        }
      }
      if (changed) setTelefones(newTelefones);
    } catch (e) {
      console.log("Erro ao verificar WhatsApp:", e);
    } finally {
      setVerificandoWhatsapp(false);
    }
  };
  
  // SMS individual modal
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsNumero, setSmsNumero] = useState("");
  const [smsTexto, setSmsTexto] = useState("");
  const [smsSending, setSmsSending] = useState(false);

  const enviarSmsIndividual = async () => {
    if (!smsTexto.trim()) { toast.error("Digite a mensagem"); return; }
    setSmsSending(true);
    try {
      const res = await axios.post(`${API}/sms/enviar`, {
        numeros: [smsNumero],
        texto: smsTexto.trim()
      });
      if (res.data.enviados > 0) {
        toast.success(`SMS enviado para ${smsNumero}`);
        setShowSmsModal(false);
        setSmsTexto("");
      } else {
        toast.error(res.data.erro || "Falha ao enviar SMS");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar SMS");
    } finally { setSmsSending(false); }
  };

  const abrirWhatsApp = (numero) => {
    const numLimpo = numero.replace(/\D/g, "");
    if (!numLimpo) { toast.error("Número inválido"); return; }
    // Get client name from form
    const nomeCliente = form.getValues("nome") || "";
    navigate(`/atendimento-whatsapp?numero=${numLimpo}&nome=${encodeURIComponent(nomeCliente)}`);
  };

  const [endereco, setEndereco] = useState({
    origem: "",
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    tipo_residencia: "NÃO INFORMADO",
    observacoes: ""
  });
  const [emprego, setEmprego] = useState({
    possui: true,
    matricula: "",
    matricula_instituidor: "",
    matricula_2: "",
    trabalha: false,
    cargo: "",
    salario: 0,
    lotacao: "",
    codigo: "",
    regimento: "",
    data_admissao: "",
    dia_pagamento: null,
    tipo_salario: ""
  });
  const [instituicoes, setInstituicoes] = useState([
    {
      id: `inst-${Date.now()}-1`,
      nome: "",
      editando: false,
      lotacao: "",
      cargo: "",
      regimento: "",
      matricula: "",
      matricula_instituidor: "",
      data_admissao: "",
      dia_pagamento: null,
      salario: "",
      tipo_salario: ""
    },
    {
      id: `inst-${Date.now()}-2`,
      nome: "",
      editando: false,
      lotacao: "",
      cargo: "",
      regimento: "",
      matricula: "",
      matricula_instituidor: "",
      data_admissao: "",
      dia_pagamento: null,
      salario: "",
      tipo_salario: ""
    }
  ]);
  const [beneficio, setBeneficio] = useState({
    possui_aposentadoria: false,
    aposentadoria_numero: "",
    aposentadoria_valor: "",
    aposentadoria_uf: "",
    aposentadoria_tipo_salario: "",
    aposentadoria_especie: "",
    aposentadoria_situacao: "",
    aposentadoria_possui_procurador: false,
    aposentadoria_possui_representante_legal: false,
    aposentadoria_pensao_alimenticia: false,
    aposentadoria_bloqueado_emprestimo: false,
    aposentadoria_elegivel_emprestimo: true,
    possui_pensao: false,
    pensao_numero: "",
    pensao_valor: "",
    pensao_uf: "",
    pensao_tipo_salario: "",
    pensao_especie: "",
    pensao_situacao: "",
    pensao_possui_procurador: false,
    pensao_possui_representante_legal: false,
    pensao_pensao_alimenticia: false,
    pensao_bloqueado_emprestimo: false,
    pensao_elegivel_emprestimo: true
  });
  const [contasBancarias, setContasBancarias] = useState([{
    id: `conta-${Date.now()}`,
    meio_recebimento: "",
    tipo_conta: "CORRENTE",
    banco: "",
    agencia: "",
    conta: "",
    variacao: "",
    principal: true
  }]);
  const [acoesOperacionais, setAcoesOperacionais] = useState({
    status: "ATIVO",
    motivo_inativo: "",
    observacoes: "",
    bancosRestricaoInterna: [], // Array de { banco_id, banco_nome, data_restricao }
    motivosRestricaoGeral: []   // Array de { motivo, data_restricao }
  });
  
  // Estado para seleção de banco com restrição
  const [novoBancoRestricao, setNovoBancoRestricao] = useState({
    banco_id: "",
    banco_nome: ""
  });
  
  // Estado para seleção de motivo de restrição geral
  const [novoMotivoRestricao, setNovoMotivoRestricao] = useState("");
  
  // Motivos de restrição carregados do backend
  const [motivosRestricao, setMotivosRestricao] = useState([]);
  const [loadingMotivos, setLoadingMotivos] = useState(false);
  
  // Modal de gerenciamento de motivos
  const [gerenciarMotivosOpen, setGerenciarMotivosOpen] = useState(false);
  const [novoMotivo, setNovoMotivo] = useState({ codigo: "", nome: "" });
  const [motivoEditando, setMotivoEditando] = useState(null);
  const [salvandoMotivo, setSalvandoMotivo] = useState(false);
  
  // CEP lookup state
  const [buscandoCEP, setBuscandoCEP] = useState(false);
  const [cepError, setCepError] = useState("");

  // Estados para importação de extrato
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importando, setImportando] = useState(false);
  const [dadosExtraidos, setDadosExtraidos] = useState(null);
  const [arquivoSelecionado, setArquivoSelecionado] = useState(null);
  const [etapaImportacao, setEtapaImportacao] = useState('upload');
  const [contratosImportados, setContratosImportados] = useState([]);
  const [tipoExtratoImportacao, setTipoExtratoImportacao] = useState('SIAPE'); // SIAPE, INSS, EXERCITO, MARINHA, AERONAUTICA, VANGUARD
  const [cpfManualImportacao, setCpfManualImportacao] = useState(''); // CPF informado manualmente para INSS

  // Configurações de tipos de extrato
  const tiposExtrato = [
    { id: 'SIAPE', nome: 'Extrato Consig. SIAPE', descricao: 'Servidores Federais Civis', cor: 'text-green-600', bgCor: 'bg-green-50', icon: '🏛️', exigeCpfManual: false },
    { id: 'SIAPE_VANGUARD', nome: 'Extrato SIAPE Vanguard', descricao: 'SIAPE via Plataforma Vanguard', cor: 'text-teal-600', bgCor: 'bg-teal-50', icon: '🏛️', exigeCpfManual: false },
    { id: 'VANGUARD_CLT', nome: 'Extrato Vanguard CLT', descricao: 'Trabalhador CLT via Plataforma Vanguard', cor: 'text-rose-600', bgCor: 'bg-rose-50', icon: '👷', exigeCpfManual: false },
    { id: 'INSS', nome: 'Extrato Consig. INSS', descricao: 'Beneficiários INSS', cor: 'text-blue-600', bgCor: 'bg-blue-50', icon: '🏥', exigeCpfManual: true },
    { id: 'VANGUARD', nome: 'Extrato Vanguard', descricao: 'Sistema Corban INSS', cor: 'text-purple-600', bgCor: 'bg-purple-50', icon: '🟣', exigeCpfManual: false },
    { id: 'EXERCITO', nome: 'Extrato Exército', descricao: 'Militares do Exército', cor: 'text-amber-600', bgCor: 'bg-amber-50', icon: '⭐', exigeCpfManual: false },
    { id: 'MARINHA', nome: 'Extrato Marinha', descricao: 'Militares da Marinha', cor: 'text-sky-600', bgCor: 'bg-sky-50', icon: '⚓', exigeCpfManual: false },
    { id: 'AERONAUTICA', nome: 'Extrato Aeronáutica', descricao: 'Militares da Aeronáutica', cor: 'text-indigo-600', bgCor: 'bg-indigo-50', icon: '✈️', exigeCpfManual: false },
  ];

  // Tipos de documentos para importação de dados
  const tiposDocumento = [
    { id: 'RG', nome: 'RG', descricao: 'Carteira de Identidade', cor: 'text-blue-600', bgCor: 'bg-blue-50', icon: '🪪' },
    { id: 'CNH', nome: 'CNH', descricao: 'Carteira de Habilitação', cor: 'text-green-600', bgCor: 'bg-green-50', icon: '🚗' },
    { id: 'CONTRA_CHEQUE', nome: 'Contra Cheque', descricao: 'Holerite/Demonstrativo', cor: 'text-purple-600', bgCor: 'bg-purple-50', icon: '💰' },
    { id: 'CONTRA_CHEQUE_PA', nome: 'Contra Cheque GOV PA', descricao: 'Servidor Estadual do Pará', cor: 'text-amber-700', bgCor: 'bg-amber-50', icon: '🏛️' },
  ];

  // Verificar se o tipo atual exige CPF manual
  const tipoExtratoAtual = tiposExtrato.find(t => t.id === tipoExtratoImportacao);
  const exigeCpfManual = tipoExtratoAtual?.exigeCpfManual || false;

  // Funções para gerenciar BANCOS com restrição interna
  const adicionarBancoRestricao = () => {
    if (!novoBancoRestricao.banco_id) return;
    
    // Verificar se já existe restrição para este banco
    const jaExiste = acoesOperacionais.bancosRestricaoInterna?.some(r => r.banco_id === novoBancoRestricao.banco_id);
    if (jaExiste) {
      alert("Este banco já está na lista de restrições");
      return;
    }
    
    const novoBanco = {
      ...novoBancoRestricao,
      data_restricao: new Date().toISOString()
    };
    
    setAcoesOperacionais(prev => ({
      ...prev,
      bancosRestricaoInterna: [...(prev.bancosRestricaoInterna || []), novoBanco]
    }));
    
    setNovoBancoRestricao({ banco_id: "", banco_nome: "" });
  };
  
  const removerBancoRestricao = (banco_id) => {
    setAcoesOperacionais(prev => ({
      ...prev,
      bancosRestricaoInterna: (prev.bancosRestricaoInterna || []).filter(r => r.banco_id !== banco_id)
    }));
  };
  
  // Funções para gerenciar MOTIVOS de restrição geral
  const adicionarMotivoRestricao = () => {
    if (!novoMotivoRestricao) return;
    
    // Verificar se já existe este motivo
    const jaExiste = acoesOperacionais.motivosRestricaoGeral?.some(r => r.motivo === novoMotivoRestricao);
    if (jaExiste) {
      alert("Este motivo já está na lista de restrições");
      return;
    }
    
    const novoMotivo = {
      motivo: novoMotivoRestricao,
      data_restricao: new Date().toISOString()
    };
    
    setAcoesOperacionais(prev => ({
      ...prev,
      motivosRestricaoGeral: [...(prev.motivosRestricaoGeral || []), novoMotivo]
    }));
    
    setNovoMotivoRestricao("");
  };
  
  const removerMotivoRestricao = (motivo) => {
    setAcoesOperacionais(prev => ({
      ...prev,
      motivosRestricaoGeral: (prev.motivosRestricaoGeral || []).filter(r => r.motivo !== motivo)
    }));
  };

  // Estados para documentos
  const [documentos, setDocumentos] = useState([]);
  const [auditorias, setAuditorias] = useState([]);
  const [auditoriaMode, setAuditoriaMode] = useState("separado");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  // Função para buscar CEP via ViaCEP
  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, "");
    if (cepLimpo.length !== 8) {
      setCepError("CEP deve ter 8 dígitos");
      return;
    }

    setBuscandoCEP(true);
    setCepError("");

    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      
      if (response.data.erro) {
        setCepError("CEP não encontrado");
        toast.error("CEP não encontrado");
        return;
      }

      setEndereco({
        ...endereco,
        cep: formatCEP(cepLimpo),
        logradouro: response.data.logradouro || "",
        bairro: response.data.bairro || "",
        cidade: response.data.localidade || "",
        estado: response.data.uf || "",
        complemento: response.data.complemento || endereco.complemento
      });

      toast.success("Endereço encontrado!");
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
      setCepError("Erro ao buscar CEP. Tente novamente.");
      toast.error("Erro ao buscar CEP");
    } finally {
      setBuscandoCEP(false);
    }
  };

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      cpf: "",
      nome: "",
      data_nascimento: "",
      naturalidade: "",
      rg: "",
      orgao_expedidor: "",
      data_emissao_rg: "",
      sexo: "",
      estado_civil: "",
      apelido: "",
      nacionalidade: "BRASILEIRO(A)",
      nome_pai: "",
      nome_mae: "",
      nome_conjuge: "",
      assina: "",
      usuario_portal: "",
      senha_portal: "",
      dupla_confirmacao_portal: false,
      origem_dados: "",
      canal_prospeccao: "",
      primeira_instituicao: "",
      segunda_instituicao: "",
      classificacao: "",
      esteira_atual: "",
    },
  });

  // Fetch lookups
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [bancosRes, instituicoesRes, estadosRes, origensRes, canaisRes, especiesRes] = await Promise.all([
          axios.get(`${API}/bancos`),
          axios.get(`${API}/lookup/instituicoes`),
          axios.get(`${API}/lookup/estados`),
          axios.get(`${API}/lookup/origens`),
          axios.get(`${API}/lookup/canais-prospeccao`),
          axios.get(`${API}/especies-inss`)
        ]);
        // Filtrar espécies por tipo
        const todasEspecies = especiesRes.data || [];
        const especiesAposentadoria = todasEspecies.filter(e => e.tipo === 'APOSENTADORIA');
        const especiesPensao = todasEspecies.filter(e => e.tipo === 'PENSAO');
        const especiesAuxilio = todasEspecies.filter(e => e.tipo === 'AUXILIO');
        
        // Formatar bancos como objetos com value e label
        const bancosData = bancosRes.data || [];
        const bancosFormatados = bancosData
          .map(b => ({
            value: b.id || `${b.codigo}`,
            label: `${b.codigo} - ${b.nome}`,
            codigo: b.codigo,
            nome: b.nome
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        
        setLookups({
          bancos: bancosFormatados,
          instituicoes: instituicoesRes.data.instituicoes,
          estados: estadosRes.data.estados,
          origens: origensRes.data.origens,
          canaisProspeccao: canaisRes.data.canais || [],
          especiesAposentadoria: especiesAposentadoria,
          especiesPensao: especiesPensao,
          especiesAuxilio: especiesAuxilio,
          todasEspecies: todasEspecies
        });
      } catch (error) {
        console.error("Erro ao carregar dados auxiliares:", error);
      }
    };
    fetchLookups();
  }, []);

  // Carregar motivos de restrição do backend
  const carregarMotivosRestricao = async () => {
    setLoadingMotivos(true);
    try {
      const response = await axios.get(`${API}/motivos-restricao`);
      const motivosFormatados = (response.data.motivos || []).map(m => ({
        value: m.codigo,
        label: `${m.codigo} - ${m.nome}`,
        codigo: m.codigo,
        nome: m.nome
      }));
      setMotivosRestricao(motivosFormatados);
    } catch (error) {
      console.error("Erro ao carregar motivos de restrição:", error);
      // Fallback para motivos padrão
      setMotivosRestricao([
        { value: "01", label: "01 - IDADE SUPERIOR PARA CONTRATO", codigo: "01", nome: "IDADE SUPERIOR PARA CONTRATO" },
        { value: "02", label: "02 - FALECIDO", codigo: "02", nome: "FALECIDO" },
        { value: "03", label: "03 - COMISSIONADO OU TEMPORÁRIO", codigo: "03", nome: "COMISSIONADO OU TEMPORÁRIO" },
        { value: "04", label: "04 - SOLICITAÇÃO JUDICIAL/EXTRA-JUDICIAL", codigo: "04", nome: "SOLICITAÇÃO JUDICIAL/EXTRA-JUDICIAL" },
        { value: "05", label: "05 - OUTROS (INFORMAR MOTIVO NO CAMPO DE OBSERVAÇÃO)", codigo: "05", nome: "OUTROS (INFORMAR MOTIVO NO CAMPO DE OBSERVAÇÃO)" },
        { value: "06", label: "06 - BLACK LIST", codigo: "06", nome: "BLACK LIST" }
      ]);
    } finally {
      setLoadingMotivos(false);
    }
  };

  useEffect(() => {
    carregarMotivosRestricao();
  }, []);

  // Funções de gerenciamento de motivos
  const handleSalvarMotivo = async () => {
    if (!novoMotivo.codigo || !novoMotivo.nome) {
      toast.error("Código e nome são obrigatórios");
      return;
    }
    
    setSalvandoMotivo(true);
    try {
      if (motivoEditando) {
        // Atualizar motivo existente
        await axios.put(`${API}/motivos-restricao/${motivoEditando.codigo}`, novoMotivo);
        toast.success("Motivo atualizado com sucesso!");
      } else {
        // Criar novo motivo
        await axios.post(`${API}/motivos-restricao`, novoMotivo);
        toast.success("Motivo criado com sucesso!");
      }
      setNovoMotivo({ codigo: "", nome: "" });
      setMotivoEditando(null);
      await carregarMotivosRestricao();
    } catch (error) {
      console.error("Erro ao salvar motivo:", error);
      toast.error(error.response?.data?.detail || "Erro ao salvar motivo");
    } finally {
      setSalvandoMotivo(false);
    }
  };

  const handleEditarMotivo = (motivo) => {
    setMotivoEditando(motivo);
    setNovoMotivo({ codigo: motivo.codigo, nome: motivo.nome });
  };

  const handleExcluirMotivo = async (codigo) => {
    if (!window.confirm("Deseja realmente excluir este motivo?")) return;
    
    try {
      await axios.delete(`${API}/motivos-restricao/${codigo}`);
      toast.success("Motivo excluído com sucesso!");
      await carregarMotivosRestricao();
    } catch (error) {
      console.error("Erro ao excluir motivo:", error);
      toast.error("Erro ao excluir motivo");
    }
  };

  const handleCancelarEdicao = () => {
    setMotivoEditando(null);
    setNovoMotivo({ codigo: "", nome: "" });
  };

  // Buscar lotações quando a primeira instituição mudar
  useEffect(() => {
    const fetchLotacoes = async () => {
      const primeiraInstituicao = instituicoes[0]?.nome;
      if (primeiraInstituicao) {
        try {
          const response = await axios.get(`${API}/lookup/lotacoes/${encodeURIComponent(primeiraInstituicao)}`);
          setLotacoesDisponiveis(response.data.lotacoes || []);
          setTemLotacoes(response.data.tem_lotacoes || false);
          // Se não tem lotações disponíveis e tinha um valor selecionado de dropdown, limpar
          if (!response.data.tem_lotacoes) {
            // Manter o valor se for texto livre
          }
        } catch (error) {
          console.error("Erro ao carregar lotações:", error);
          setLotacoesDisponiveis([]);
          setTemLotacoes(false);
        }
      } else {
        setLotacoesDisponiveis([]);
        setTemLotacoes(false);
      }
    };
    fetchLotacoes();
  }, [instituicoes[0]?.nome]);

  // Fetch client data if editing
  useEffect(() => {
    if (isEditing) {
      const fetchCliente = async () => {
        setLoading(true);
        try {
          const response = await axios.get(`${API}/clientes/${id}`);
          const cliente = response.data;
          
          // Set form values
          form.reset({
            cpf: cliente.cpf || "",
            nome: cliente.nome || "",
            data_nascimento: cliente.data_nascimento || "",
            naturalidade: cliente.naturalidade || "",
            rg: cliente.rg || "",
            orgao_expedidor: cliente.orgao_expedidor || "",
            data_emissao_rg: cliente.data_emissao_rg || "",
            sexo: cliente.sexo || "",
            estado_civil: cliente.estado_civil || "",
            apelido: cliente.apelido || "",
            nacionalidade: cliente.nacionalidade || "BRASILEIRO(A)",
            nome_pai: cliente.nome_pai || "",
            nome_mae: cliente.nome_mae || "",
            nome_conjuge: cliente.nome_conjuge || "",
            assina: cliente.assina || "",
            usuario_portal: cliente.usuario_portal || "",
            senha_portal: cliente.senha_portal || "",
            dupla_confirmacao_portal: Boolean(cliente.dupla_confirmacao_portal),
            origem_dados: cliente.origem_dados || "",
            canal_prospeccao: cliente.canal_prospeccao || "",
            primeira_instituicao: cliente.primeira_instituicao || "",
            segunda_instituicao: cliente.segunda_instituicao || "",
            classificacao: cliente.classificacao || "",
            esteira_atual: cliente.esteira_atual || "",
          });
          
          // Set other state
          setTelefones(cliente.telefones || []);
          // Verificar WhatsApp automaticamente
          if (cliente.telefones?.length > 0) {
            setTimeout(() => verificarWhatsappTodos(cliente.telefones), 500);
          }
          setEmails(cliente.emails || []);
          setTelefonesExcluidos(cliente.telefones_excluidos || []);
          setEmailsExcluidos(cliente.emails_excluidos || []);
          setEndereco(cliente.endereco || endereco);
          setEmprego(cliente.emprego || emprego);
          
          // Carrega instituições do cliente
          if (cliente.instituicoes && cliente.instituicoes.length > 0) {
            // Garante que todas as instituições tenham a propriedade editando
            const instsComEditando = cliente.instituicoes.map(inst => ({
              ...inst,
              editando: true
            }));
            setInstituicoes(instsComEditando);
          } else if (cliente.primeira_instituicao) {
            // Compatibilidade com dados antigos - extrair nome se for objeto
            const extrairNome = (inst) => {
              if (!inst) return null;
              if (typeof inst === 'object') return inst.nome || inst.instituicao || null;
              return inst;
            };
            const primeiraInstNome = extrairNome(cliente.primeira_instituicao);
            const segundaInstNome = extrairNome(cliente.segunda_instituicao);
            
            const insts = [];
            if (primeiraInstNome) {
              insts.push({id: `inst-1`, nome: primeiraInstNome, editando: true});
            }
            if (segundaInstNome) {
              insts.push({id: `inst-2`, nome: segundaInstNome, editando: true});
            }
            setInstituicoes(insts);
          }
          
          // Formata os valores de benefício para exibição
          const beneficioCarregado = cliente.beneficio || beneficio;
          if (beneficioCarregado.aposentadoria_valor) {
            beneficioCarregado.aposentadoria_valor = formatCurrency(String(Math.round(beneficioCarregado.aposentadoria_valor * 100)));
          }
          if (beneficioCarregado.pensao_valor) {
            beneficioCarregado.pensao_valor = formatCurrency(String(Math.round(beneficioCarregado.pensao_valor * 100)));
          }
          setBeneficio(beneficioCarregado);
          
          // Se cliente tem contas, usa elas; senão mantém a conta inicial vazia
          if (cliente.contas_bancarias && cliente.contas_bancarias.length > 0) {
            setContasBancarias(cliente.contas_bancarias);
          }
          setAcoesOperacionais(cliente.acoes_operacionais || acoesOperacionais);
          setFotoPreview(cliente.foto_url || null);
          
          // Carregar documentos do cliente
          carregarDocumentos(id);
          carregarAuditorias(id);
          
          // Verificar se cliente tem contratos (INTERNO/EXTERNO)
          verificarClienteInterno(id);
        } catch (error) {
          console.error("Erro ao carregar cliente:", error);
          toast.error("Erro ao carregar dados do cliente");
          navigate("/clientes");
        } finally {
          setLoading(false);
        }
      };
      fetchCliente();
    }
  }, [id, isEditing]);

  // Verifica se cliente já tem contratos existentes (INTERNO/EXTERNO)
  const verificarClienteInterno = async (clienteId) => {
    try {
      const response = await axios.get(`${API}/contratos/cliente/${clienteId}`);
      const temContratos = response.data?.total_contratos > 0;
      setClienteInterno(temContratos);
    } catch (error) {
      console.error("Erro ao verificar contratos do cliente:", error);
      setClienteInterno(false);
    }
  };

  const onSubmit = async (data) => {
    setSaving(true);
    // Bloquear readicionar número que está no histórico de excluídos
    {
      const exclDigits = new Set((telefonesExcluidos || []).map(e => (e.numero || "").replace(/\D/g, "")).filter(Boolean));
      const conflito = (telefones || []).find(t => t?.numero && exclDigits.has(t.numero.replace(/\D/g, "")));
      if (conflito) {
        toast.error(`O número ${conflito.numero} está no histórico de excluídos. Clique em "Restaurar" antes de cadastrá-lo novamente.`);
        setSaving(false);
        return;
      }
    }
    try {
      // Converte valores de moeda formatada para número
      const beneficioFormatado = {
        ...beneficio,
        aposentadoria_valor: parseCurrency(beneficio.aposentadoria_valor),
        pensao_valor: parseCurrency(beneficio.pensao_valor)
      };

      // Limpar campos enum vazios para usar valores default do backend
      const dadosLimpos = { ...data };
      const camposEnum = ['sexo', 'estado_civil', 'assina', 'nacionalidade', 'classificacao', 'esteira_atual'];
      camposEnum.forEach(campo => {
        if (dadosLimpos[campo] === '' || dadosLimpos[campo] === undefined) {
          delete dadosLimpos[campo];
        }
      });

      const usuarioCadastrou = JSON.parse(localStorage.getItem("usuario") || "null");
      const payload = {
        ...dadosLimpos,
        telefones: telefones.filter((t) => t != null),
        emails: emails.filter((e) => e != null),
        telefones_excluidos: telefonesExcluidos,
        emails_excluidos: emailsExcluidos,
        endereco,
        emprego,
        instituicoes,
        primeira_instituicao: instituicoes[0]?.nome || "",
        segunda_instituicao: instituicoes[1]?.nome || "",
        beneficio: beneficioFormatado,
        contas_bancarias: contasBancarias,
        acoes_operacionais: acoesOperacionais,
        foto_url: fotoPreview,
        empresa_id: dadosLimpos.empresa_id || JSON.parse(localStorage.getItem("empresa") || "null")?.id || null,
        criado_por_id: dadosLimpos.criado_por_id || usuarioCadastrou?.id || null,
        criado_por_nome: dadosLimpos.criado_por_nome || usuarioCadastrou?.nome || usuarioCadastrou?.email || null
      };

      if (isEditing) {
        await axios.put(`${API}/clientes/${id}`, payload);
        toast.success("Cliente atualizado com sucesso!");
        // Permanece na mesma página
      } else {
        const response = await axios.post(`${API}/clientes`, payload);
        toast.success("Cliente cadastrado com sucesso!");
        // Redireciona para a página de edição do cliente recém-criado
        navigate(`/clientes/${response.data.id}/editar`, { replace: true });
      }
    } catch (error) {
      console.error("Erro ao salvar cliente:", error);
      let errorMsg = "Erro ao salvar cliente";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMsg = detail.msg || JSON.stringify(detail);
        }
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Telefone handlers
  const addTelefone = () => {
    setTelefones([...telefones, {
      id: crypto.randomUUID(),
      numero: "",
      tipo: "MÓVEL",
      operadora: "",
      origem: "",
      observacao: "",
      whatsapp: false
    }]);
  };

  const updateTelefone = (index, field, value) => {
    const updated = [...telefones];
    updated[index][field] = value;
    setTelefones(updated);
  };

  const removeTelefone = (index) => {
    setTelefones(telefones.filter((_, i) => i !== index));
  };

  // Abrir confirmação de exclusão/desvínculo de número (vai para histórico)
  const solicitarExclusaoTelefone = (index) => {
    const numero = telefones[index]?.numero || "";
    if (!numero) {
      toast.error("Não há número para excluir neste campo");
      return;
    }
    setConfirmExclusao({ open: true, index, numero, motivo: "" });
  };

  // Confirmar exclusão: move número para histórico de excluídos e remove do cliente
  const confirmarExclusaoTelefone = () => {
    const { index, numero, motivo } = confirmExclusao;
    const usuario = JSON.parse(localStorage.getItem("usuario") || "null");
    const registro = {
      numero,
      motivo: motivo || "Desvinculado manualmente",
      excluido_em: new Date().toISOString(),
      excluido_por: usuario?.nome || usuario?.email || "Sistema"
    };
    setTelefonesExcluidos((prev) => [...prev, registro]);
    // Limpar o campo do telefone (mantém os 5 slots fixos vazios)
    const novos = [...telefones];
    if (novos[index]) {
      novos[index] = {
        ...novos[index],
        numero: "",
        tel_whatsapp: "",
        lid_whatsapp: "",
        whatsapp: false,
        bloqueado: false,
        principal: false
      };
    }
    setTelefones(novos);
    setConfirmExclusao({ open: false, index: null, numero: "", motivo: "" });
    toast.success("Número movido para o histórico. Não será reimportado. Clique em Salvar Cliente para confirmar.");
  };

  // Email handlers
  const addEmail = () => {
    setEmails([...emails, {
      id: crypto.randomUUID(),
      email: "",
      origem: "",
      observacao: "",
      principal: emails.length === 0
    }]);
  };

  const updateEmail = (index, field, value) => {
    const updated = [...emails];
    updated[index][field] = value;
    setEmails(updated);
  };

  const removeEmail = (index) => {
    setEmails(emails.filter((_, i) => i !== index));
  };

  // Mover e-mail para o histórico de excluídos (espelha o comportamento dos telefones)
  const excluirEmailParaHistorico = (index) => {
    const email = emails[index]?.email || "";
    if (!email) { toast.error("Não há e-mail para excluir neste campo"); return; }
    if (!window.confirm(`Mover o e-mail "${email}" para o histórico de excluídos?`)) return;
    const usuario = JSON.parse(localStorage.getItem("usuario") || "null");
    const registro = {
      email,
      motivo: "Removido manualmente",
      excluido_em: new Date().toISOString(),
      excluido_por: usuario?.nome || usuario?.email || "Sistema"
    };
    setEmailsExcluidos((prev) => [...prev, registro]);
    const novos = [...emails];
    if (novos[index]) {
      novos[index] = { ...novos[index], email: "", senha: "", observacao: "", principal: false };
    }
    setEmails(novos);
    toast.success("E-mail movido para o histórico. Clique em Salvar Cliente para confirmar.");
  };

  // Conta Bancária handlers
  const addContaBancaria = () => {
    setContasBancarias([...contasBancarias, {
      id: crypto.randomUUID(),
      tipo_conta: "CORRENTE",
      banco: "",
      meio_recebimento: "NÃO INFORMADO",
      agencia: "",
      variacao: "",
      conta: "",
      principal: contasBancarias.length === 0
    }]);
  };

  const updateContaBancaria = (index, field, value) => {
    const updated = [...contasBancarias];
    updated[index][field] = value;
    setContasBancarias(updated);
  };

  const removeContaBancaria = (index) => {
    setContasBancarias(contasBancarias.filter((_, i) => i !== index));
  };

  // Função para calcular idade detalhada
  const calcularIdade = (dataNascimento) => {
    if (!dataNascimento) return null;
    
    const hoje = new Date();
    const nascimento = new Date(dataNascimento);
    
    if (isNaN(nascimento.getTime())) return null;
    
    let anos = hoje.getFullYear() - nascimento.getFullYear();
    let meses = hoje.getMonth() - nascimento.getMonth();
    let dias = hoje.getDate() - nascimento.getDate();
    
    if (dias < 0) {
      meses--;
      const ultimoDiaMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth(), 0).getDate();
      dias += ultimoDiaMesAnterior;
    }
    
    if (meses < 0) {
      anos--;
      meses += 12;
    }
    
    return { anos, meses, dias };
  };

  // Função para calcular signo do zodíaco
  const calcularSigno = (dataNascimento) => {
    if (!dataNascimento) return null;
    
    const nascimento = new Date(dataNascimento);
    if (isNaN(nascimento.getTime())) return null;
    
    const dia = nascimento.getDate();
    const mes = nascimento.getMonth() + 1; // getMonth() retorna 0-11
    
    const signos = [
      { nome: "CAPRICÓRNIO", emoji: "♑", inicio: [12, 22], fim: [1, 19] },
      { nome: "AQUÁRIO", emoji: "♒", inicio: [1, 20], fim: [2, 18] },
      { nome: "PEIXES", emoji: "♓", inicio: [2, 19], fim: [3, 20] },
      { nome: "ÁRIES", emoji: "♈", inicio: [3, 21], fim: [4, 19] },
      { nome: "TOURO", emoji: "♉", inicio: [4, 20], fim: [5, 20] },
      { nome: "GÊMEOS", emoji: "♊", inicio: [5, 21], fim: [6, 20] },
      { nome: "CÂNCER", emoji: "♋", inicio: [6, 21], fim: [7, 22] },
      { nome: "LEÃO", emoji: "♌", inicio: [7, 23], fim: [8, 22] },
      { nome: "VIRGEM", emoji: "♍", inicio: [8, 23], fim: [9, 22] },
      { nome: "LIBRA", emoji: "♎", inicio: [9, 23], fim: [10, 22] },
      { nome: "ESCORPIÃO", emoji: "♏", inicio: [10, 23], fim: [11, 21] },
      { nome: "SAGITÁRIO", emoji: "♐", inicio: [11, 22], fim: [12, 21] },
    ];
    
    for (const signo of signos) {
      const [mesInicio, diaInicio] = signo.inicio;
      const [mesFim, diaFim] = signo.fim;
      
      // Caso especial para Capricórnio (atravessa o ano)
      if (signo.nome === "CAPRICÓRNIO") {
        if ((mes === 12 && dia >= 22) || (mes === 1 && dia <= 19)) {
          return signo;
        }
      } else {
        if ((mes === mesInicio && dia >= diaInicio) || (mes === mesFim && dia <= diaFim)) {
          return signo;
        }
      }
    }
    
    return null;
  };

  // Foto handler
  const handleFotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Format CPF
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, "");
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4").slice(0, 14);
  };

  // Format currency (Real brasileiro)
  const formatCurrency = (value) => {
    // Remove tudo que não é número
    let numbers = value.toString().replace(/\D/g, "");
    
    if (!numbers || numbers === "0") return "";
    
    // Remove zeros à esquerda (exceto se for só zeros)
    numbers = numbers.replace(/^0+/, "") || "0";
    
    // Se tiver menos de 3 dígitos, não tem centavos ainda
    if (numbers.length <= 2) {
      return numbers;
    }
    
    // Separa reais e centavos
    const cents = numbers.slice(-2);
    let reais = numbers.slice(0, -2);
    
    // Adiciona separadores de milhar
    reais = reais.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    
    return `${reais},${cents}`;
  };

  // Parse currency to number
  const parseCurrency = (value) => {
    if (!value) return 0;
    // Remove pontos de milhar e troca vírgula por ponto
    const cleanValue = value.toString().replace(/\./g, "").replace(",", ".");
    return parseFloat(cleanValue) || 0;
  };

  // Format phone
  const formatPhone = (value) => {
    // Remove tudo que não é número e limita a 11 dígitos (DDD + número)
    const numbers = value.replace(/\D/g, "");
    return numbers.slice(0, 11);
  };

  // Format CEP
  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, "");
    return numbers.replace(/(\d{5})(\d{3})/, "$1-$2").slice(0, 9);
  };

  // ==================== FUNÇÕES DE IMPORTAÇÃO DE EXTRATO ====================
  
  const handleArquivoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error("Apenas arquivos PDF são aceitos");
        return;
      }
      setArquivoSelecionado(file);
    }
  };

  const processarExtrato = async () => {
    if (!arquivoSelecionado) {
      toast.error("Selecione um arquivo PDF");
      return;
    }

    // Validar CPF para tipos que exigem CPF manual (INSS)
    if (exigeCpfManual && !cpfManualImportacao.trim()) {
      toast.error("Para importar extrato INSS, é obrigatório informar o CPF do beneficiário");
      return;
    }

    // Validar formato do CPF
    if (exigeCpfManual && cpfManualImportacao.trim()) {
      const cpfLimpo = cpfManualImportacao.replace(/\D/g, '');
      if (cpfLimpo.length !== 11) {
        toast.error("CPF inválido. O CPF deve ter 11 dígitos");
        return;
      }
    }

    setImportando(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('arquivo', arquivoSelecionado);
      // Enviar o CPF manual se houver
      if (cpfManualImportacao.trim()) {
        formDataUpload.append('cpf_manual', cpfManualImportacao.trim());
      }
      // Enviar o tipo de extrato
      formDataUpload.append('tipo_extrato', tipoExtratoImportacao);

      const response = await axios.post(`${API}/extratos/processar`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        // Se CPF manual foi informado, usar ele nos dados extraídos
        const dadosExtraidosAtualizados = { ...response.data.dados };
        if (cpfManualImportacao.trim()) {
          if (!dadosExtraidosAtualizados.cliente) {
            dadosExtraidosAtualizados.cliente = {};
          }
          dadosExtraidosAtualizados.cliente.cpf = formatarCPF(cpfManualImportacao.replace(/\D/g, ''));
        }
        
        setDadosExtraidos(dadosExtraidosAtualizados);
        setEtapaImportacao('preview');
        toast.success("Extrato processado! Verifique os dados extraídos.");
      }
    } catch (error) {
      console.error("Erro ao processar extrato:", error);
      toast.error(error.response?.data?.detail || "Erro ao processar extrato");
    } finally {
      setImportando(false);
    }
  };

  // Função para formatar CPF
  const formatarCPF = (cpf) => {
    const numeros = cpf.replace(/\D/g, '');
    return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const confirmarImportacao = async () => {
    if (!dadosExtraidos) return;

    setImportando(true);
    try {
      // Adicionar o tipo de extrato aos dados
      // Obter empresa_id do localStorage para multi-tenancy
      const empresaAtual = JSON.parse(localStorage.getItem("empresa") || "null");
      const usuarioAtual = JSON.parse(localStorage.getItem("usuario") || "null");
      
      const dadosComTipo = {
        ...dadosExtraidos,
        tipo_extrato: tipoExtratoImportacao,
        empresa_id: empresaAtual?.id || null,
        atendente_id: usuarioAtual?.id || null
      };
      
      // Chamar endpoint para salvar cliente e contratos diretamente
      const response = await axios.post(`${API}/extratos/importar`, dadosComTipo);
      
      if (response.data.success) {
        const { cliente_id, cliente_nome, contratos_novos, contratos_existentes, mensagem } = response.data;
        
        // Salvar o PDF do extrato como documento do cliente
        if (cliente_id && arquivoSelecionado) {
          try {
            const formData = new FormData();
            formData.append('arquivo', arquivoSelecionado);
            formData.append('tipo', 'EXTRATO');
            formData.append('descricao', `Extrato ${tipoExtratoImportacao} - ${new Date().toLocaleDateString('pt-BR')}`);
            
            await axios.post(`${API}/clientes/${cliente_id}/documentos`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (docError) {
            console.error("Erro ao salvar PDF como documento:", docError);
            // Não bloqueia o fluxo principal se falhar salvar o documento
          }
        }
        
        setEtapaImportacao('success');
        toast.success(mensagem);
        
        // Fechar dialog e redirecionar para edição do cliente
        setTimeout(() => {
          setImportDialogOpen(false);
          resetImportacao();
          // Redirecionar para a página de edição do cliente criado
          if (cliente_id) {
            navigate(`/clientes/${cliente_id}/editar`);
          }
        }, 1500);
      }
    } catch (error) {
      console.error("Erro ao importar:", error);
      toast.error(error.response?.data?.detail || "Erro ao importar dados");
    } finally {
      setImportando(false);
    }
  };

  const resetImportacao = () => {
    setArquivoSelecionado(null);
    setDadosExtraidos(null);
    setEtapaImportacao('upload');
    setCpfManualImportacao('');
  };

  // ==================== IMPORTAÇÃO DE DOCUMENTOS (RG, CNH, etc) ====================
  
  // Processar documento com IA para extrair dados e preencher formulário
  const processarDocumentoParaFormulario = async (tipoDocumento) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > 10 * 1024 * 1024) {
        toast.error("Arquivo muito grande. Máximo 10MB");
        return;
      }

      setImportando(true);
      toast.info(`Processando ${tipoDocumento} com IA...`);

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('tipo', tipoDocumento);

        const response = await axios.post(`${API}/documentos/processar`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.success) {
          const dados = response.data.dados;
          let camposPreenchidos = 0;

          // Mapear dados extraídos para os campos do formulário
          if (tipoDocumento === 'RG' || tipoDocumento === 'CNH') {
            // Nome
            const nome = dados.nome || dados.nome_completo;
            if (nome && !form.getValues('nome')) {
              form.setValue('nome', nome.toUpperCase());
              camposPreenchidos++;
            }
            // CPF
            const cpf = dados.cpf || dados.cpf_numero;
            if (cpf && !form.getValues('cpf')) {
              const cpfFormatado = String(cpf).replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
              form.setValue('cpf', cpfFormatado);
              camposPreenchidos++;
            }
            // RG
            const rg = dados.rg || dados.numero_rg || dados.identidade;
            if (rg && !form.getValues('rg')) {
              form.setValue('rg', String(rg));
              camposPreenchidos++;
            }
            // Órgão Expedidor
            const orgaoExp = dados.orgao_expedidor || dados.orgao_emissor || dados.emissor;
            if (orgaoExp && !form.getValues('orgao_expedidor')) {
              form.setValue('orgao_expedidor', orgaoExp);
              camposPreenchidos++;
            }
            // Data de Nascimento
            const dataNasc = dados.data_nascimento || dados.nascimento;
            if (dataNasc && !form.getValues('data_nascimento')) {
              // Converter DD/MM/YYYY para YYYY-MM-DD
              const partes = String(dataNasc).split('/');
              if (partes.length === 3) {
                const dataFormatada = `${partes[2]}-${partes[1].padStart(2, '0')}-${partes[0].padStart(2, '0')}`;
                form.setValue('data_nascimento', dataFormatada);
                camposPreenchidos++;
              }
            }
            // Data Emissão RG
            const dataEmissao = dados.data_emissao_rg || dados.data_emissao || dados.emissao;
            if (dataEmissao && !form.getValues('data_emissao_rg')) {
              const partes = String(dataEmissao).split('/');
              if (partes.length === 3) {
                const dataFormatada = `${partes[2]}-${partes[1].padStart(2, '0')}-${partes[0].padStart(2, '0')}`;
                form.setValue('data_emissao_rg', dataFormatada);
                camposPreenchidos++;
              }
            }
            // Naturalidade
            const naturalidade = dados.naturalidade || dados.local_nascimento;
            if (naturalidade && !form.getValues('naturalidade')) {
              form.setValue('naturalidade', naturalidade.toUpperCase());
              camposPreenchidos++;
            }
            // Nome do Pai
            const nomePai = dados.nome_pai || dados.filiacao_pai || dados.pai;
            if (nomePai && !form.getValues('nome_pai')) {
              form.setValue('nome_pai', nomePai.toUpperCase());
              camposPreenchidos++;
            }
            // Nome da Mãe
            const nomeMae = dados.nome_mae || dados.filiacao_mae || dados.mae;
            if (nomeMae && !form.getValues('nome_mae')) {
              form.setValue('nome_mae', nomeMae.toUpperCase());
              camposPreenchidos++;
            }
          }

          if (tipoDocumento === 'COMPROVANTE_RESIDENCIA') {
            const cep = dados.cep || dados.codigo_postal;
            if (cep) {
              const cepLimpo = String(cep).replace(/\D/g, '');
              setEndereco(prev => ({ ...prev, cep: cepLimpo.replace(/(\d{5})(\d{3})/, '$1-$2') }));
              camposPreenchidos++;
            }
            const logradouro = dados.logradouro || dados.rua || dados.endereco;
            if (logradouro) {
              setEndereco(prev => ({ ...prev, logradouro: logradouro.toUpperCase() }));
              camposPreenchidos++;
            }
            const numero = dados.numero || dados.num;
            if (numero) {
              setEndereco(prev => ({ ...prev, numero: String(numero) }));
              camposPreenchidos++;
            }
            const complemento = dados.complemento || dados.apto || dados.bloco;
            if (complemento) {
              setEndereco(prev => ({ ...prev, complemento: complemento.toUpperCase() }));
              camposPreenchidos++;
            }
            const bairro = dados.bairro || dados.setor;
            if (bairro) {
              setEndereco(prev => ({ ...prev, bairro: bairro.toUpperCase() }));
              camposPreenchidos++;
            }
            const cidade = dados.cidade || dados.municipio || dados.localidade;
            if (cidade) {
              setEndereco(prev => ({ ...prev, cidade: cidade.toUpperCase() }));
              camposPreenchidos++;
            }
            const estado = dados.estado || dados.uf;
            if (estado) {
              setEndereco(prev => ({ ...prev, estado: estado.toUpperCase() }));
              camposPreenchidos++;
            }
          }

          if (tipoDocumento === 'CONTRA_CHEQUE' || tipoDocumento === 'CONTRA_CHEQUE_PA') {
            const nome = dados.nome || dados.nome_completo;
            if (nome && !form.getValues('nome')) {
              form.setValue('nome', nome.toUpperCase());
              camposPreenchidos++;
            }
            const cpf = dados.cpf || dados.cpf_numero;
            if (cpf && !form.getValues('cpf')) {
              const cpfFormatado = String(cpf).replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
              form.setValue('cpf', cpfFormatado);
              camposPreenchidos++;
            }
            if (dados.matricula) {
              setEmprego(prev => ({ ...prev, matricula: dados.matricula }));
              camposPreenchidos++;
            }
            if (dados.matricula_instituidor) {
              setEmprego(prev => ({ ...prev, matricula_instituidor: dados.matricula_instituidor }));
              setInstituicoes(prev => {
                const alvoEncontrado = prev.findIndex(inst => {
                  const ehPensao = inst.regimento === "PENSIONISTA" || /PENS[AÃ]O/i.test(inst.lotacao || "");
                  return ehPensao || (dados.matricula && inst.matricula === dados.matricula);
                });
                const alvo = alvoEncontrado >= 0 ? alvoEncontrado : 0;
                return prev.map((inst, index) => {
                  if (index === alvo) {
                    return {
                      ...inst,
                      matricula: dados.matricula || inst.matricula,
                      matricula_instituidor: dados.matricula_instituidor,
                      regimento: "PENSIONISTA"
                    };
                  }
                  return inst;
                });
              });
              camposPreenchidos++;
            }
            if (dados.cargo) {
              setEmprego(prev => ({ ...prev, cargo: dados.cargo.toUpperCase() }));
              camposPreenchidos++;
            }
            if (dados.lotacao) {
              setEmprego(prev => ({ ...prev, lotacao: dados.lotacao.toUpperCase() }));
              camposPreenchidos++;
            }
            if (dados.empresa) {
              setEmprego(prev => ({ ...prev, orgao: dados.empresa.toUpperCase() }));
              camposPreenchidos++;
            }
            if (dados.salario_bruto) {
              const salario = typeof dados.salario_bruto === 'number' ? dados.salario_bruto : parseFloat(String(dados.salario_bruto).replace(/\./g, '').replace(',', '.') || 0);
              setEmprego(prev => ({ ...prev, salario_bruto: salario }));
              camposPreenchidos++;
            }
          }

          if (camposPreenchidos > 0) {
            toast.success(`${camposPreenchidos} campo(s) preenchido(s) automaticamente!`);
          } else {
            toast.info("Documento processado, mas nenhum campo novo foi identificado");
            console.log("Dados extraídos:", dados);
          }
        }
      } catch (error) {
        console.error("Erro ao processar documento:", error);
        console.error("Response:", error.response?.data);
        toast.error(error.response?.data?.detail || "Erro ao processar documento. Tente novamente.");
      } finally {
        setImportando(false);
      }
    };

    input.click();
  };

  // Função para salvar contratos após criar/atualizar cliente
  const salvarContratosImportados = async (clienteId) => {
    if (contratosImportados.length === 0) return;

    try {
      for (const contrato of contratosImportados) {
        let valorParcela = contrato.valor_parcela;
        if (typeof valorParcela === 'string') {
          valorParcela = parseFloat(valorParcela.replace(/\./g, "").replace(",", ".").replace("R$", "").trim() || 0);
        }

        const novoContrato = {
          cliente_id: clienteId,
          numero_contrato: String(contrato.numero_contrato || ""),
          tipo: "EXTERNO",
          tipo_operacao: contrato.tipo_operacao?.toUpperCase()?.includes("CARTÃO") ? "CARTAO_CONSIGNADO" : "EMPRESTIMO",
          banco_codigo: contrato.banco_codigo || "",
          banco_nome: (contrato.banco_nome || "").toUpperCase(),
          valor_parcela: valorParcela || 0,
          quantidade_parcelas: parseInt(contrato.quantidade_parcelas) || 0,
          parcelas_restantes: parseInt(contrato.parcelas_restantes) || 0,
          parcela_atual: (parseInt(contrato.quantidade_parcelas) || 0) - (parseInt(contrato.parcelas_restantes) || 0),
          data_inicio: contrato.data_inicio || "",
          data_fim: contrato.data_fim || "",
          situacao: "ATIVO"
        };

        await axios.post(`${API}/contratos`, novoContrato);
      }
      
      toast.success(`${contratosImportados.length} contrato(s) importado(s) para o Monitoramento!`);
      setContratosImportados([]);
    } catch (error) {
      console.error("Erro ao importar contratos:", error);
      toast.error("Erro ao importar alguns contratos");
    }
  };

  // ==================== FUNÇÕES DE DOCUMENTOS ====================

  // Carregar documentos do cliente
  const carregarDocumentos = async (clienteId) => {
    try {
      const response = await axios.get(`${API}/clientes/${clienteId}/documentos`);
      // Não listar conversas de WhatsApp aqui (elas têm lugar próprio na aba Auditoria)
      setDocumentos((response.data.documentos || []).filter((d) => d.tipo !== "CONVERSA_WHATSAPP"));
    } catch (error) {
      console.error("Erro ao carregar documentos:", error);
    }
  };

  const carregarAuditorias = async (clienteId) => {
    try {
      const response = await axios.get(`${API}/wpp/auditoria/${clienteId}`);
      setAuditorias(response.data || []);
    } catch { setAuditorias([]); }
  };

  // Upload de documento
  const handleUploadDocumento = async (tipo) => {
    if (!isEditing || !id) {
      toast.error("Salve o cliente primeiro antes de enviar documentos");
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > 10 * 1024 * 1024) {
        toast.error("Arquivo muito grande. Máximo 10MB");
        return;
      }

      setUploadingDoc(true);
      try {
        const formData = new FormData();
        formData.append('arquivo', file);
        formData.append('tipo', tipo);

        const response = await axios.post(`${API}/clientes/${id}/documentos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.success) {
          toast.success("Documento enviado com sucesso!");
          carregarDocumentos(id);
        }
      } catch (error) {
        console.error("Erro ao enviar documento:", error);
        toast.error("Erro ao enviar documento");
      } finally {
        setUploadingDoc(false);
      }
    };
    input.click();
  };

  // Upload de documento com descrição customizada
  const handleUploadDocumentoComDescricao = async (tipo) => {
    if (!isEditing || !id) {
      toast.error("Salve o cliente primeiro antes de enviar documentos");
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = (tipo === 'SELFIE') ? 'image/*' : ((tipo === 'EXTRATO' || tipo === 'EXTRATO_INSS') ? '.pdf' : 'image/*,.pdf');
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > 10 * 1024 * 1024) {
        toast.error("Arquivo muito grande. Máximo 10MB");
        return;
      }

      const descricoesPadrao = {
        "RG": "RG",
        "CNH": "CNH",
        "SELFIE": "Selfie",
        "COMPROVANTE_RESIDENCIA": "Comprovante de Residência",
        "CONTRA_CHEQUE": "Contra Cheque",
        "EXTRATO": "Extrato Consignação SIAPE",
        "EXTRATO_INSS": "Extrato Consignação INSS",
        "OUTRO": "Documento"
      };

      let descricao = descricoesPadrao[tipo] || tipo;
      
      if (tipo === "OUTRO") {
        descricao = prompt("Digite uma descrição para o documento:", "") || "Documento";
      }

      setUploadingDoc(true);
      try {
        // Somente EXTRATO (SIAPE) e EXTRATO_INSS podem extrair dados
        const tiposComExtracao = ["EXTRATO", "EXTRATO_INSS"];
        let deveExtrair = tiposComExtracao.includes(tipo);

        // Verificar limite de 2 extrações por mês
        if (deveExtrair) {
          try {
            const limiteResp = await axios.get(`${API}/clientes/${id}/extracoes-mes`);
            if (!limiteResp.data.pode_extrair) {
              toast.warning(`Limite de ${limiteResp.data.limite} extrações/mês atingido. O arquivo será apenas anexado.`);
              deveExtrair = false;
            }
          } catch (err) {
            console.error("Erro ao verificar limite:", err);
          }
        }

        if (deveExtrair) {
          toast.info(`Extraindo dados do ${descricao}...`);
          
          const formDataProcessar = new FormData();
          formDataProcessar.append('file', file);
          formDataProcessar.append('tipo', tipo);
          
          const processResponse = await axios.post(`${API}/documentos/processar`, formDataProcessar, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          if (processResponse.data.success) {
            const dadosExtraidos = processResponse.data.dados;
            
            const updateResponse = await axios.post(`${API}/documentos/atualizar-cliente/${id}`, {
              tipo: tipo,
              dados: dadosExtraidos
            });
            
            if (updateResponse.data.success) {
              const mensagem = updateResponse.data.campos_atualizados 
                ? `Dados extraídos! ${updateResponse.data.campos_atualizados} campo(s) atualizado(s)`
                : "Dados extraídos!";
              toast.success(mensagem);
              
              const formData = new FormData();
              formData.append('arquivo', file);
              formData.append('tipo', tipo);
              formData.append('descricao', `${descricao} - ${new Date().toLocaleDateString('pt-BR')}`);
              formData.append('processado', 'true');
              formData.append('campos_atualizados', String(updateResponse.data.campos_atualizados || 0));
              
              await axios.post(`${API}/clientes/${id}/documentos`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
              
              carregarDocumentos(id);
              window.location.reload();
            } else {
              throw new Error(updateResponse.data.detail || "Erro ao atualizar cliente");
            }
          } else {
            throw new Error("Erro ao processar documento");
          }
        } else {
          // Upload simples - apenas anexar sem extração
          const formData = new FormData();
          formData.append('arquivo', file);
          formData.append('tipo', tipo);
          formData.append('descricao', `${descricao} - ${new Date().toLocaleDateString('pt-BR')}`);

          const response = await axios.post(`${API}/clientes/${id}/documentos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          if (response.data.success) {
            toast.success("Documento anexado com sucesso!");
            carregarDocumentos(id);
          }
        }
      } catch (error) {
        console.error("Erro ao enviar documento:", error);
        toast.error(error.response?.data?.detail || error.message || "Erro ao enviar documento");
      } finally {
        setUploadingDoc(false);
      }
    };
    input.click();
  };

  // Excluir documento
  const handleExcluirDocumento = async (docId) => {
    if (!confirm("Tem certeza que deseja excluir este documento?")) return;

    try {
      await axios.delete(`${API}/clientes/${id}/documentos/${docId}`);
      toast.success("Documento excluído!");
      carregarDocumentos(id);
    } catch (error) {
      console.error("Erro ao excluir documento:", error);
      toast.error("Erro ao excluir documento");
    }
  };

  // Visualizar documento
  const handleVisualizarDocumento = async (doc) => {
    try {
      const response = await axios.get(`${API}/clientes/${id}/documentos/${doc.id}`);
      setPreviewDoc(response.data);
    } catch (error) {
      console.error("Erro ao carregar documento:", error);
      toast.error("Erro ao carregar documento para visualização");
    }
  };

  // Formatar tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(0)) + ' ' + sizes[i];
  };

  // Formatar data do documento
  const formatDataDocumento = (dataStr) => {
    if (!dataStr) return "-";
    try {
      const data = new Date(dataStr);
      return data.toLocaleDateString('pt-BR');
    } catch {
      return dataStr;
    }
  };

  // Modificar onSubmit para salvar contratos após salvar cliente
  const onSubmitWithContratos = async (data) => {
    setSaving(true);
    // Bloquear readicionar número que está no histórico de excluídos
    {
      const exclDigits = new Set((telefonesExcluidos || []).map(e => (e.numero || "").replace(/\D/g, "")).filter(Boolean));
      const conflito = (telefones || []).find(t => t?.numero && exclDigits.has(t.numero.replace(/\D/g, "")));
      if (conflito) {
        toast.error(`O número ${conflito.numero} está no histórico de excluídos. Clique em "Restaurar" antes de cadastrá-lo novamente.`);
        setSaving(false);
        return;
      }
    }
    
    try {
      const empresaAtual = JSON.parse(localStorage.getItem("empresa") || "null");
      const usuarioAtual = JSON.parse(localStorage.getItem("usuario") || "null");
      const payload = {
        ...data,
        telefones: telefones.filter((t) => t != null),
        emails: emails.filter((e) => e != null),
        telefones_excluidos: telefonesExcluidos,
        emails_excluidos: emailsExcluidos,
        endereco,
        emprego,
        instituicoes,
        beneficio,
        contas_bancarias: contasBancarias,
        acoes_operacionais: acoesOperacionais,
        foto: fotoPreview,
        empresa_id: data.empresa_id || empresaAtual?.id || null,
        criado_por_id: data.criado_por_id || usuarioAtual?.id || null,
        criado_por_nome: data.criado_por_nome || usuarioAtual?.nome || usuarioAtual?.email || null
      };

      let clienteId;
      if (isEditing) {
        await axios.put(`${API}/clientes/${id}`, payload);
        clienteId = id;
        toast.success("Cliente atualizado com sucesso!");
      } else {
        const response = await axios.post(`${API}/clientes`, payload);
        clienteId = response.data.id;
        toast.success("Cliente cadastrado com sucesso!");
      }

      // Salvar contratos importados se houver
      if (contratosImportados.length > 0 && clienteId) {
        await salvarContratosImportados(clienteId);
      }

      if (!isEditing) {
        navigate(`/clientes/${clienteId}/editar`, { replace: true });
      }
    } catch (error) {
      console.error("Erro ao salvar cliente:", error);
      let errorMsg = "Erro ao salvar cliente";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') errorMsg = detail;
        else if (Array.isArray(detail)) errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        else if (typeof detail === 'object') errorMsg = detail.msg || JSON.stringify(detail);
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Formatação de moeda para exibição
  const formatCurrencyDisplay = (value) => {
    if (!value && value !== 0) return "-";
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/clientes">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          {/* Foto do Cliente no Header */}
          <div className="relative">
            <Avatar className="h-16 w-16 border-2 border-primary/20">
              <AvatarImage src={fotoPreview || ""} />
              <AvatarFallback className="bg-primary/10 text-primary text-xl font-semibold">
                {form.watch("nome")?.charAt(0)?.toUpperCase() || "?"}
              </AvatarFallback>
            </Avatar>
            <Label htmlFor="foto-header" className="absolute -bottom-1 -right-1 cursor-pointer">
              <div className="h-6 w-6 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary/90 shadow-md">
                <Upload className="h-3 w-3" />
              </div>
            </Label>
            <Input
              id="foto-header"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFotoChange}
            />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="form-title">
              {isEditing ? "Editar Cliente" : "Novo Cliente"}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isEditing ? "Atualize os dados do cliente" : "Preencha os dados para cadastrar um novo cliente"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isEditing && (
            <Button type="button" variant="outline" onClick={buscarNovaVida} disabled={buscandoNV}
              className="gap-2 border-blue-500 text-blue-600 hover:bg-blue-50" data-testid="btn-nova-vida">
              {buscandoNV ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {buscandoNV ? "Consultando..." : "Buscar dados (Nova Vida)"}
            </Button>
          )}
          {/* Botão Importar Extrato com Dropdown - apenas para novo cliente */}
          {!isEditing && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2 border-green-500 text-green-600 hover:bg-green-50">
                  <FileUp className="h-4 w-4" />
                  Importar Extrato
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel className="text-sm font-semibold text-gray-700">
                  Selecione o tipo de extrato
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/consultar-cadastrar')}
                  className="cursor-pointer flex items-center gap-3 p-3 bg-purple-50"
                >
                  <Search className="h-5 w-5 text-purple-600" />
                  <div className="flex-1">
                    <p className="font-medium text-purple-700">Consulta Vanguard por CPF</p>
                    <p className="text-xs text-gray-500">Consulta, confere e atualiza cadastro e contratos</p>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {tiposExtrato.map((tipo) => (
                  <DropdownMenuItem
                    key={tipo.id}
                    onClick={() => {
                      setTipoExtratoImportacao(tipo.id);
                      setImportDialogOpen(true);
                    }}
                    className={cn("cursor-pointer flex items-center gap-3 p-3", tipo.bgCor)}
                  >
                    <span className="text-xl">{tipo.icon}</span>
                    <div className="flex-1">
                      <p className={cn("font-medium", tipo.cor)}>{tipo.nome}</p>
                      <p className="text-xs text-gray-500">{tipo.descricao}</p>
                    </div>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-sm font-semibold text-gray-700">
                  Importar dados de documentos
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {tiposDocumento.map((tipo) => (
                  <DropdownMenuItem
                    key={tipo.id}
                    onClick={() => processarDocumentoParaFormulario(tipo.id)}
                    className={cn("cursor-pointer flex items-center gap-3 p-3", tipo.bgCor)}
                    disabled={importando}
                  >
                    <span className="text-xl">{tipo.icon}</span>
                    <div className="flex-1">
                      <p className={cn("font-medium", tipo.cor)}>{tipo.nome}</p>
                      <p className="text-xs text-gray-500">{tipo.descricao}</p>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Dialog de Importação */}
          <Dialog open={importDialogOpen} onOpenChange={(open) => {
            if (!open) resetImportacao();
            setImportDialogOpen(open);
          }}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="text-2xl">{tiposExtrato.find(t => t.id === tipoExtratoImportacao)?.icon}</span>
                  <span className={tiposExtrato.find(t => t.id === tipoExtratoImportacao)?.cor}>
                    Importar {tiposExtrato.find(t => t.id === tipoExtratoImportacao)?.nome}
                  </span>
                </DialogTitle>
                <DialogDescription>
                  Faça upload de um PDF de extrato para preencher automaticamente os dados do cliente e importar os contratos.
                </DialogDescription>
              </DialogHeader>

              {etapaImportacao === 'upload' && (
                <div className="space-y-6 py-4">
                  {/* Seletor de tipo dentro do modal */}
                  <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg">
                    {tiposExtrato.map((tipo) => (
                      <Button
                        key={tipo.id}
                        variant={tipoExtratoImportacao === tipo.id ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setTipoExtratoImportacao(tipo.id);
                          if (!tipo.exigeCpfManual) {
                            setCpfManualImportacao('');
                          }
                        }}
                        className={cn(
                          "gap-2",
                          tipoExtratoImportacao === tipo.id && tipo.bgCor
                        )}
                      >
                        <span>{tipo.icon}</span>
                        {tipo.id}
                      </Button>
                    ))}
                  </div>

                  {/* Campo CPF obrigatório para INSS */}
                  {exigeCpfManual && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                      <div className="flex items-center gap-2 text-blue-700">
                        <AlertCircle className="h-5 w-5" />
                        <span className="font-medium">CPF Obrigatório para Extrato INSS</span>
                      </div>
                      <p className="text-sm text-blue-600">
                        O extrato do INSS não contém o CPF do beneficiário. Por favor, informe o CPF antes de processar.
                      </p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Digite o CPF do beneficiário"
                          value={cpfManualImportacao}
                          onChange={(e) => {
                            // Aplicar máscara de CPF
                            let valor = e.target.value.replace(/\D/g, '');
                            if (valor.length <= 11) {
                              valor = valor.replace(/(\d{3})(\d)/, '$1.$2');
                              valor = valor.replace(/(\d{3})(\d)/, '$1.$2');
                              valor = valor.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                            }
                            setCpfManualImportacao(valor);
                          }}
                          maxLength={14}
                          className="flex-1 bg-white border-blue-300 focus:border-blue-500"
                        />
                        {cpfManualImportacao && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setCpfManualImportacao('')}
                            className="text-blue-600 hover:bg-blue-100"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      {cpfManualImportacao && cpfManualImportacao.replace(/\D/g, '').length === 11 && (
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm">CPF válido</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className={cn(
                    "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
                    tipoExtratoImportacao === 'SIAPE' ? "border-green-300 hover:border-green-400" :
                    tipoExtratoImportacao === 'INSS' ? "border-blue-300 hover:border-blue-400" :
                    tipoExtratoImportacao === 'VANGUARD' ? "border-purple-300 hover:border-purple-400" :
                    tipoExtratoImportacao === 'VANGUARD_CLT' ? "border-rose-300 hover:border-rose-400" :
                    tipoExtratoImportacao === 'EXERCITO' ? "border-amber-300 hover:border-amber-400" :
                    tipoExtratoImportacao === 'MARINHA' ? "border-sky-300 hover:border-sky-400" :
                    "border-indigo-300 hover:border-indigo-400"
                  )}>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleArquivoChange}
                      className="hidden"
                      id="extrato-upload-cliente"
                    />
                    <label htmlFor="extrato-upload-cliente" className="cursor-pointer">
                      <Upload className={cn(
                        "h-12 w-12 mx-auto mb-4",
                        tiposExtrato.find(t => t.id === tipoExtratoImportacao)?.cor || "text-slate-400"
                      )} />
                      <p className="text-lg font-medium text-slate-700">
                        {arquivoSelecionado ? arquivoSelecionado.name : "Clique para selecionar o PDF"}
                      </p>
                      <p className="text-sm text-slate-500 mt-2">
                        Apenas arquivos PDF são aceitos • {tiposExtrato.find(t => t.id === tipoExtratoImportacao)?.descricao}
                      </p>
                    </label>
                  </div>

                  {arquivoSelecionado && (
                    <div className={cn(
                      "flex items-center justify-between p-4 rounded-lg",
                      tiposExtrato.find(t => t.id === tipoExtratoImportacao)?.bgCor || "bg-green-50"
                    )}>
                      <div className="flex items-center gap-3">
                        <FileText className={cn(
                          "h-8 w-8",
                          tiposExtrato.find(t => t.id === tipoExtratoImportacao)?.cor || "text-green-600"
                        )} />
                        <div>
                          <p className={cn(
                            "font-medium",
                            tiposExtrato.find(t => t.id === tipoExtratoImportacao)?.cor || "text-green-700"
                          )}>{arquivoSelecionado.name}</p>
                          <p className="text-sm text-slate-600">{(arquivoSelecionado.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setArquivoSelecionado(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}

                  <Button 
                    onClick={processarExtrato} 
                    disabled={!arquivoSelecionado || importando || (exigeCpfManual && (!cpfManualImportacao || cpfManualImportacao.replace(/\D/g, '').length !== 11))}
                    className={cn("w-full gap-2")}
                    size="lg"
                  >
                    {importando ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Processando com IA...
                        </>
                      ) : (
                        <>
                          <Search className="h-5 w-5" />
                          Extrair Dados do PDF
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {etapaImportacao === 'preview' && dadosExtraidos && (
                  <div className="space-y-6 py-4">
                    {/* Dados do Cliente */}
                    <Card className="border-blue-200 bg-blue-50/50">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-blue-600" />
                          Dados do Cliente
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <Label className="text-xs text-slate-500">Nome</Label>
                          <p className="font-medium">{dadosExtraidos.cliente?.nome || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">CPF</Label>
                          <p className="font-medium">{dadosExtraidos.cliente?.cpf || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">{dadosExtraidos.tipo_extrato === 'VANGUARD_CLT' ? 'Empresa (Empregador)' : 'Órgão/Instituição'}</Label>
                          <p className="font-medium">{dadosExtraidos.cliente?.orgao || dadosExtraidos.cliente?.empresa || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">{dadosExtraidos.tipo_extrato === 'VANGUARD_CLT' ? 'CNPJ' : 'Matrícula/Benefício'}</Label>
                          <p className="font-medium">{dadosExtraidos.tipo_extrato === 'VANGUARD_CLT' ? (dadosExtraidos.cliente?.cnpj || '-') : (dadosExtraidos.cliente?.matricula || dadosExtraidos.cliente?.numero_beneficio || '-')}</p>
                        </div>
                        {dadosExtraidos.cliente?.matricula_instituidor && (
                          <div>
                            <Label className="text-xs text-slate-500">Matrícula do instituidor</Label>
                            <p className="font-medium">{dadosExtraidos.cliente.matricula_instituidor}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Margem Consignável (CLT mostra Salário + Margem Livre; demais mostram compulsória/facultativa/cartão) */}
                    {dadosExtraidos.tipo_extrato === 'VANGUARD_CLT' ? (
                      <Card className="border-purple-200 bg-purple-50/50">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-purple-600" />
                            Margem CLT
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <Label className="text-xs text-slate-500">Salário</Label>
                            <p className="font-medium text-green-600">{formatCurrencyDisplay(dadosExtraidos.cliente?.salario)}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Margem Livre (35%)</Label>
                            <p className="font-medium text-green-600">{formatCurrencyDisplay(dadosExtraidos.cliente?.margem_consignavel)}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">CNAE</Label>
                            <p className="font-medium">{dadosExtraidos.cliente?.cnae || '-'}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ) : (dadosExtraidos.margem && (
                      <Card className="border-purple-200 bg-purple-50/50">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-purple-600" />
                            Margem Consignável
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <Label className="text-xs text-slate-500">Líquida Compulsória</Label>
                            <p className="font-medium text-green-600">{formatCurrencyDisplay(dadosExtraidos.margem.liquida_compulsoria)}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Líquida Facultativa</Label>
                            <p className="font-medium text-green-600">{formatCurrencyDisplay(dadosExtraidos.margem.liquida_facultativa)}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Líquida Cartão</Label>
                            <p className="font-medium text-green-600">{formatCurrencyDisplay(dadosExtraidos.margem.liquida_cartao)}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                    {/* Contratos Extraídos */}
                    <Card className="border-orange-200 bg-orange-50/50">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <FileText className="h-4 w-4 text-orange-600" />
                          Contratos Encontrados ({dadosExtraidos.contratos?.length || 0})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="max-h-64 overflow-y-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-xs">Banco</TableHead>
                                <TableHead className="text-xs">Contrato</TableHead>
                                <TableHead className="text-xs">Parcela</TableHead>
                                <TableHead className="text-xs">Qtd/Rest</TableHead>
                                <TableHead className="text-xs">Tipo</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {dadosExtraidos.contratos?.map((contrato, idx) => (
                                <TableRow key={idx}>
                                  <TableCell className="text-xs font-medium">{contrato.banco_nome}</TableCell>
                                  <TableCell className="text-xs">{contrato.numero_contrato}</TableCell>
                                  <TableCell className="text-xs">{formatCurrencyDisplay(contrato.valor_parcela)}</TableCell>
                                  <TableCell className="text-xs">{contrato.quantidade_parcelas}/{contrato.parcelas_restantes || '?'}</TableCell>
                                  <TableCell className="text-xs">{contrato.tipo_operacao || '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>

                    <div className="flex gap-3">
                      <Button 
                        variant="outline" 
                        onClick={() => setEtapaImportacao('upload')}
                        className="flex-1"
                      >
                        Voltar
                      </Button>
                      <Button 
                        onClick={confirmarImportacao}
                        disabled={importando}
                        className="flex-1 bg-green-600 hover:bg-green-700 gap-2"
                      >
                        {importando ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Salvando...
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4" />
                            Importar e Salvar
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {etapaImportacao === 'success' && (
                  <div className="space-y-6 py-8 text-center">
                    <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="h-8 w-8 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-green-700">Importação Concluída!</h3>
                      <p className="text-slate-600 mt-2">Cliente e contratos salvos com sucesso.</p>
                      <p className="text-sm text-slate-500 mt-1">Redirecionando para o cadastro...</p>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          )}
          
          {/* Botão Salvar Cliente no Header */}
          <Button 
            onClick={form.handleSubmit(onSubmit, () => { toast.error('Não foi possível salvar: verifique os campos obrigatórios na aba Pessoal.'); })} 
            disabled={saving}
            className="gap-2 bg-primary hover:bg-primary/90"
            data-testid="save-client-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "Salvando..." : "Salvar Cliente"}
          </Button>
        </div>
      </div>

      {/* Form Tabs */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="principal" className="gap-2" data-testid="tab-principal">
                <User className="h-4 w-4" />
                <span className="hidden md:inline">Cadastro</span>
              </TabsTrigger>
              <TabsTrigger value="documentos" className="gap-2" data-testid="tab-documentos">
                <FileImage className="h-4 w-4" />
                <span className="hidden md:inline">Documentos</span>
              </TabsTrigger>
              <TabsTrigger value="auditoria" className="gap-2" data-testid="tab-auditoria">
                <ShieldCheck className="h-4 w-4" />
                <span className="hidden md:inline">Auditoria</span>
              </TabsTrigger>
            </TabsList>

            {/* Página Principal Unificada: Pessoal + Contato + Endereço + Emprego + Bancário */}
            <TabsContent value="principal" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Dados Pessoais</CardTitle>
                  <CardDescription>Informações básicas do cliente</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <FormField
                      control={form.control}
                      name="cpf"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>CPF *</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="000.000.000-00"
                              onChange={(e) => field.onChange(formatCPF(e.target.value))}
                              maxLength={14}
                              data-testid="input-cpf"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="nome"
                      render={({ field }) => (
                        <FormItem className="lg:col-span-2">
                          <FormLabel>Nome Completo *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Nome completo" data-testid="input-nome" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="apelido"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Gosta de ser chamado</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Apelido" data-testid="input-apelido" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="data_nascimento"
                      render={({ field }) => {
                        const idade = calcularIdade(field.value);
                        const signo = calcularSigno(field.value);
                        return (
                          <FormItem className="lg:col-span-2">
                            <FormLabel>Data de Nascimento *</FormLabel>
                            <div className="flex items-center gap-3 flex-wrap">
                              <FormControl>
                                <Input {...field} type="date" className="w-40" data-testid="input-data-nascimento" />
                              </FormControl>
                              {idade && (
                                <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-2 rounded-md">
                                  {idade.anos} anos {idade.meses} meses {idade.dias} dias
                                </span>
                              )}
                              {signo && (
                                <span className="text-sm font-semibold text-purple-600 bg-purple-50 px-3 py-2 rounded-md">
                                  Signo: {signo.emoji} {signo.nome}
                                </span>
                              )}
                            </div>
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={form.control}
                      name="naturalidade"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Naturalidade *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Cidade natal" data-testid="input-naturalidade" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="rg"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>RG *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Número do RG" data-testid="input-rg" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="orgao_expedidor"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Órgão Expedidor *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Ex: SSP/PA" data-testid="input-orgao" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="data_emissao_rg"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Data de Emissão *</FormLabel>
                          <FormControl>
                            <Input {...field} type="date" data-testid="input-data-emissao" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="sexo"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Sexo *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-sexo">
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {sexoOptions.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="estado_civil"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Estado Civil *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-estado-civil">
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {estadoCivilOptions.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="nacionalidade"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nacionalidade *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-nacionalidade">
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {nacionalidadeOptions.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="nome_pai"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nome do Pai *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Nome completo do pai" data-testid="input-nome-pai" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="nome_mae"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nome da Mãe *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Nome completo da mãe" data-testid="input-nome-mae" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="nome_conjuge"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nome do Cônjuge</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Nome do cônjuge (se houver)" data-testid="input-conjuge" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="assina"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Assina</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-assina">
                                <SelectValue placeholder="Assina" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {assinaOptions.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />

                    {/* Dados de acesso ao portal */}
                    <div className="grid gap-2 md:grid-cols-3">
                      <FormField
                        control={form.control}
                        name="usuario_portal"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormLabel className="text-red-600">usuário</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="" data-testid="input-usuario-portal" className="border-red-400" />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="senha_portal"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormLabel className="text-red-600">senha</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="" data-testid="input-senha-portal" className="border-red-400" />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="dupla_confirmacao_portal"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Acesso com dupla confirmação?</FormLabel>
                            <Select
                              value={field.value ? "SIM" : "NAO"}
                              onValueChange={(valor) => field.onChange(valor === "SIM")}
                            >
                              <FormControl>
                                <SelectTrigger data-testid="select-dupla-confirmacao-portal">
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="SIM">Sim</SelectItem>
                                <SelectItem value="NAO">Não</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>Informe se o portal pede código, aplicativo ou outra validação.</FormDescription>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <FormField
                      control={form.control}
                      name="origem_dados"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Origem dos Dados *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-origem">
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {lookups.origens.map((origem) => (
                                <SelectItem key={origem} value={origem}>{origem}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="canal_prospeccao"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Canal de Prospecção</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-canal-prospeccao">
                                <SelectValue placeholder="Como o cliente chegou?" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {lookups.canaisProspeccao.map((canal) => (
                                <SelectItem key={canal} value={canal}>{canal}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="classificacao"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel>Tipo de Lead</FormLabel>
                            {isEditing && (
                              <Badge className={clienteInterno ? "bg-blue-100 text-blue-800" : "bg-orange-100 text-orange-800"}>
                                {clienteInterno ? "INTERNO" : "EXTERNO"}
                              </Badge>
                            )}
                          </div>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-classificacao">
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {classificacaoOptions.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="esteira_atual"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Esteira Atual</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-esteira">
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {esteiraOptions.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            <Separator className="my-2" />
            {/* Seção: Contatos */}
              <div className="grid gap-6 lg:grid-cols-2 items-start">
                {/* COLUNA ESQUERDA — Telefonia (Ativos + Excluídos) */}
                <div className="space-y-4">
                {/* Telefones - Layout Compacto */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Telefones</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-0">
                    {/* Lista de Telefones Fixos - 5 campos */}
                    {[0, 1, 2, 3, 4].map((index) => (
                      <div key={index} className="border-b last:border-b-0 py-4">
                        <div className="flex items-center gap-3 flex-wrap xl:flex-nowrap">
                          <div className="w-40 shrink-0">
                            <p className="text-sm font-semibold text-slate-600 mb-1">Tel. {index + 1}</p>
                            <Input
                              value={telefones[index]?.numero || ""}
                              onChange={(e) => {
                                const newTelefones = [...telefones];
                                if (!newTelefones[index]) {
                                  newTelefones[index] = {
                                    id: crypto.randomUUID(),
                                    numero: "",
                                    tipo: "MÓVEL",
                                    operadora: "",
                                    origem: "",
                                    observacao: "",
                                    whatsapp: true,
                                    bloqueado: false,
                                    principal: index === 0
                                  };
                                }
                                newTelefones[index].numero = formatPhone(e.target.value);
                                setTelefones(newTelefones);
                              }}
                              placeholder="DDD + Número (ex: 91985690870)"
                              className={cn(
                                "text-lg font-mono h-11 w-full border border-slate-200 rounded-md px-2 bg-white focus-visible:ring-2",
                                telefones[index]?.bloqueado && "text-red-500 border-red-300"
                              )}
                              data-testid={`telefone-numero-${index}`}
                            />
                          </div>
                          <div className="w-40 shrink-0">
                            <p className="text-sm font-semibold text-slate-600 mb-1">LID WhatsApp</p>
                            {(() => {
                              const numLimpo = telefones[index]?.numero?.replace(/\D/g, "") || "";
                              const wStatus = whatsappStatus[numLimpo] || {};
                              const telWpp = wStatus.jid?.replace("@s.whatsapp.net", "") || telefones[index]?.tel_whatsapp || "";
                              const lid = wStatus.lid || telefones[index]?.lid_whatsapp || "";
                              const temInfo = telWpp || lid;
                              return (
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1">
                                    <span className="text-[9px] text-gray-400 w-10 shrink-0">Tel:</span>
                                    <Input
                                      value={telWpp}
                                      onChange={(e) => {
                                        const newTelefones = [...telefones];
                                        if (newTelefones[index]) {
                                          newTelefones[index].tel_whatsapp = e.target.value;
                                          setTelefones(newTelefones);
                                        }
                                      }}
                                      placeholder="-"
                                      className={cn(
                                        "h-6 font-mono text-[11px] border rounded px-2 py-0",
                                        telWpp ? "border-green-300 bg-green-50 text-green-700" : "border-slate-200"
                                      )}
                                      readOnly={!!wStatus.jid}
                                      data-testid={`telefone-telwpp-${index}`}
                                    />
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-[9px] text-gray-400 w-10 shrink-0">LID:</span>
                                    <Input
                                      value={lid}
                                      onChange={(e) => {
                                        const newTelefones = [...telefones];
                                        if (newTelefones[index]) {
                                          newTelefones[index].lid_whatsapp = e.target.value;
                                          setTelefones(newTelefones);
                                        }
                                      }}
                                      placeholder="-"
                                      className={cn(
                                        "h-6 font-mono text-[11px] border rounded px-2 py-0",
                                        lid ? "border-blue-300 bg-blue-50 text-blue-700" : "border-slate-200"
                                      )}
                                      readOnly={!!wStatus.lid}
                                      data-testid={`telefone-lid-${index}`}
                                    />
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                          <div className="flex items-center gap-1.5 pt-5 ml-auto shrink-0 [&_button]:h-9 [&_button]:w-9 [&_svg]:h-4 [&_svg]:w-4">
                            {/* Botão Principal */}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className={cn(
                                "h-12 w-12 rounded-full shadow-md",
                                telefones[index]?.principal 
                                  ? "bg-yellow-400 text-white hover:bg-yellow-500" 
                                  : "bg-gray-100 text-gray-400 hover:bg-yellow-100 hover:text-yellow-500"
                              )}
                              onClick={() => {
                                const newTelefones = [...telefones];
                                // Desmarcar todos como principal
                                newTelefones.forEach((t) => {
                                  if (t) t.principal = false;
                                });
                                if (!newTelefones[index]) {
                                  newTelefones[index] = {
                                    id: crypto.randomUUID(),
                                    numero: "",
                                    tipo: "MÓVEL",
                                    operadora: "",
                                    origem: "",
                                    observacao: "",
                                    whatsapp: true,
                                    bloqueado: false,
                                    principal: true
                                  };
                                } else {
                                  newTelefones[index].principal = true;
                                }
                                setTelefones(newTelefones);
                              }}
                              title={telefones[index]?.principal ? "Telefone Principal" : "Marcar como Principal"}
                            >
                              <Star className="h-6 w-6" />
                            </Button>
                            {/* Botão Bloqueado */}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className={cn(
                                "h-12 w-12 rounded-full shadow-md",
                                telefones[index]?.bloqueado 
                                  ? "bg-red-500 text-white hover:bg-red-600" 
                                  : "bg-white border-2 border-red-500 text-red-500 hover:bg-red-50"
                              )}
                              onClick={() => {
                                const newTelefones = [...telefones];
                                if (!newTelefones[index]) {
                                  newTelefones[index] = {
                                    id: crypto.randomUUID(),
                                    numero: "",
                                    tipo: "MÓVEL",
                                    operadora: "",
                                    origem: "",
                                    observacao: "",
                                    whatsapp: true,
                                    bloqueado: true,
                                    principal: false
                                  };
                                } else {
                                  newTelefones[index].bloqueado = !newTelefones[index].bloqueado;
                                }
                                setTelefones(newTelefones);
                              }}
                              title={telefones[index]?.bloqueado ? "Desbloquear telefone" : "Bloquear telefone"}
                            >
                              <PhoneOff className="h-6 w-6" />
                            </Button>
                            {/* Botão WhatsApp */}
                            {(() => {
                              const numLimpo = telefones[index]?.numero?.replace(/\D/g, "") || "";
                              const wStatus = whatsappStatus[numLimpo] || {};
                              const temWpp = wStatus.tem_whatsapp;
                              const interagiu = wStatus.interagiu;
                              const verificado = temWpp !== undefined && temWpp !== null;

                              return (
                                <div className="relative">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className={cn(
                                      "h-12 w-12 rounded-full shadow-md transition-all",
                                      verificado && temWpp && interagiu
                                        ? "bg-green-500 text-white hover:bg-green-600 ring-2 ring-green-700 ring-offset-1"
                                        : verificado && temWpp
                                        ? "bg-green-400 text-white hover:bg-green-500"
                                        : verificado && !temWpp
                                        ? "bg-gray-200 text-gray-400"
                                        : telefones[index]?.whatsapp
                                        ? "bg-green-500 text-white hover:bg-green-600 ring-2 ring-green-700 ring-offset-1" 
                                        : "bg-gray-100 text-gray-400 hover:bg-green-100 hover:text-green-500"
                                    )}
                                    onClick={() => {
                                      if (numLimpo && (temWpp || telefones[index]?.whatsapp)) {
                                        abrirWhatsApp(numLimpo);
                                      } else if (!verificado) {
                                        const newTelefones = [...telefones];
                                        if (newTelefones[index]) {
                                          newTelefones[index].whatsapp = !newTelefones[index].whatsapp;
                                          setTelefones(newTelefones);
                                        }
                                      }
                                    }}
                                    title={
                                      verificado && temWpp && interagiu
                                        ? "WhatsApp ativo - Clique para abrir conversa"
                                        : verificado && temWpp
                                        ? "Tem WhatsApp - Clique para abrir conversa"
                                        : verificado && !temWpp
                                        ? "Não tem WhatsApp"
                                        : telefones[index]?.whatsapp
                                        ? "Abrir WhatsApp"
                                        : "Marcar como WhatsApp"
                                    }
                                    data-testid={`telefone-whatsapp-${index}`}
                                  >
                                    <MessageCircle className="h-6 w-6" />
                                  </Button>
                                  {verificado && temWpp && interagiu && (
                                    <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center" title="Já interagiu no WhatsApp">
                                      <span className="text-[8px] text-white font-bold">!</span>
                                    </span>
                                  )}
                                  {verificandoWhatsapp && !verificado && numLimpo && (
                                    <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-yellow-400 border-2 border-white animate-pulse" title="Verificando..."></span>
                                  )}
                                </div>
                              );
                            })()}
                            {/* Botão SMS */}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-12 w-12 rounded-full bg-purple-500 text-white hover:bg-purple-600 shadow-md"
                              onClick={() => {
                                if (telefones[index]?.numero) {
                                  const numLimpo = telefones[index].numero.replace(/\D/g, "");
                                  setSmsNumero(numLimpo);
                                  setSmsTexto("");
                                  setShowSmsModal(true);
                                } else {
                                  toast.error("Digite um número de telefone primeiro");
                                }
                              }}
                              title="Enviar SMS"
                              data-testid={`telefone-sms-${index}`}
                            >
                              <MessageSquare className="h-6 w-6" />
                            </Button>
                            {/* Botão Ligar - AZUL */}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-12 w-12 rounded-full bg-blue-500 text-white hover:bg-blue-600 shadow-md"
                              onClick={() => {
                                if (telefones[index]?.numero) {
                                  window.open(`tel:${telefones[index].numero.replace(/\D/g, "")}`, "_blank");
                                }
                              }}
                              title="Ligar"
                            >
                              <Phone className="h-6 w-6" />
                            </Button>
                            {/* Botão Excluir Número (desvincular + histórico) */}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-12 w-12 rounded-full bg-white border-2 border-rose-500 text-rose-600 hover:bg-rose-50 shadow-md"
                              onClick={() => solicitarExclusaoTelefone(index)}
                              title="Excluir número (vai para o histórico e não será reimportado)"
                              data-testid={`telefone-excluir-${index}`}
                            >
                              <Trash2 className="h-6 w-6" />
                            </Button>
                          </div>
                        </div>
                        {/* Obs movida para a linha de baixo (largura total) */}
                        <div className="mt-2 flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-600 shrink-0">Obs.:</p>
                          <Input
                            value={telefones[index]?.observacao || ""}
                            onChange={(e) => {
                              const newTelefones = [...telefones];
                              if (!newTelefones[index]) {
                                newTelefones[index] = {
                                  id: crypto.randomUUID(),
                                  numero: "",
                                  tipo: "MÓVEL",
                                  operadora: "",
                                  origem: "",
                                  observacao: "",
                                  whatsapp: true,
                                  bloqueado: false,
                                  principal: false
                                };
                              }
                              newTelefones[index].observacao = e.target.value;
                              setTelefones(newTelefones);
                            }}
                            placeholder="Observação..."
                            className="h-10 w-full border border-slate-200 rounded-md px-3 bg-white focus-visible:ring-2"
                            data-testid={`telefone-obs-${index}`}
                          />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Histórico de Números Excluídos (não serão reimportados) */}
                {telefonesExcluidos.length > 0 && (
                  <Card className="border-rose-200" data-testid="historico-telefones-excluidos">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2 text-rose-700">
                        <Trash2 className="h-4 w-4" />
                        Números Excluídos do Histórico ({telefonesExcluidos.length})
                      </CardTitle>
                      <CardDescription>
                        Estes números foram desvinculados do CPF e NÃO serão readicionados em novas importações.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {telefonesExcluidos.map((ex, i) => (
                        <div key={i} className="flex items-center justify-between border border-rose-100 rounded-md px-3 py-2 bg-rose-50/50">
                          <div className="flex flex-col">
                            <span className="font-mono text-sm font-semibold text-rose-700">{ex.numero}</span>
                            <span className="text-xs text-slate-500">
                              {ex.motivo || "—"}
                              {ex.excluido_por ? ` • por ${ex.excluido_por}` : ""}
                              {ex.excluido_em ? ` • ${new Date(ex.excluido_em).toLocaleDateString("pt-BR")}` : ""}
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                            onClick={() => setTelefonesExcluidos((prev) => prev.filter((_, idx) => idx !== i))}
                            title="Restaurar: permite que o número volte a ser importado"
                            data-testid={`restaurar-telefone-${i}`}
                          >
                            Restaurar
                          </Button>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
                </div>

                {/* COLUNA DIREITA — E-mails (Ativos + Excluídos) */}
                <div className="space-y-4">
                {/* Emails - Layout Compacto */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">E-mails</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-0">
                    {/* Lista de E-mails Fixos */}
                    {[0, 1, 2].map((index) => (
                      <div key={index} className="border-b last:border-b-0 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-64">
                            <p className="text-sm font-semibold text-slate-600 mb-1">E-mail {index + 1}</p>
                            <Input
                              type="email"
                              value={emails[index]?.email || ""}
                              onChange={(e) => {
                                const newEmails = [...emails];
                                if (!newEmails[index]) {
                                  newEmails[index] = {
                                    id: crypto.randomUUID(),
                                    email: "",
                                    senha: "",
                                    origem: "",
                                    observacao: "",
                                    principal: index === 0
                                  };
                                }
                                newEmails[index].email = e.target.value;
                                setEmails(newEmails);
                              }}
                              placeholder="email@exemplo.com"
                              className="text-lg h-12 border border-slate-200 rounded-md px-3 bg-white focus-visible:ring-2"
                              data-testid={`email-${index}`}
                            />
                          </div>
                          <div className="w-36">
                            <p className="text-sm font-semibold text-slate-600 mb-1">Senha</p>
                            <Input
                              type="text"
                              value={emails[index]?.senha || ""}
                              onChange={(e) => {
                                const newEmails = [...emails];
                                if (!newEmails[index]) {
                                  newEmails[index] = {
                                    id: crypto.randomUUID(),
                                    email: "",
                                    senha: "",
                                    origem: "",
                                    observacao: "",
                                    principal: index === 0
                                  };
                                }
                                newEmails[index].senha = e.target.value;
                                setEmails(newEmails);
                              }}
                              placeholder="••••••"
                              className="text-lg font-mono h-12 border border-slate-200 rounded-md px-3 bg-white focus-visible:ring-2"
                              data-testid={`email-senha-${index}`}
                            />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-slate-600 mb-1">Obs.:</p>
                            <Input
                              type="text"
                              value={emails[index]?.observacao || ""}
                              onChange={(e) => {
                                const newEmails = [...emails];
                                if (!newEmails[index]) {
                                  newEmails[index] = {
                                    id: crypto.randomUUID(),
                                    email: "",
                                    senha: "",
                                    origem: "",
                                    observacao: "",
                                    principal: index === 0
                                  };
                                }
                                newEmails[index].observacao = e.target.value;
                                setEmails(newEmails);
                              }}
                              placeholder="Observação..."
                              className="h-12 border border-slate-200 rounded-md px-3 bg-white focus-visible:ring-2"
                              data-testid={`email-obs-${index}`}
                            />
                          </div>
                          <div className="flex items-center gap-2 pt-5">
                            {/* Botão Principal */}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className={cn(
                                "h-12 w-12 rounded-full shadow-md",
                                emails[index]?.principal 
                                  ? "bg-blue-500 text-white hover:bg-blue-600" 
                                  : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                              )}
                              onClick={() => {
                                const newEmails = [...emails];
                                // Desmarcar todos como principal
                                newEmails.forEach((e, i) => {
                                  if (e) e.principal = false;
                                });
                                if (!newEmails[index]) {
                                  newEmails[index] = {
                                    id: crypto.randomUUID(),
                                    email: "",
                                    senha: "",
                                    origem: "",
                                    observacao: "",
                                    principal: true
                                  };
                                } else {
                                  newEmails[index].principal = true;
                                }
                                setEmails(newEmails);
                              }}
                              title={emails[index]?.principal ? "E-mail Principal" : "Marcar como Principal"}
                            >
                              <Star className="h-6 w-6" />
                            </Button>
                            {/* Botão Enviar E-mail */}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-12 w-12 rounded-full bg-slate-800 text-white hover:bg-slate-900 shadow-md"
                              onClick={() => {
                                if (emails[index]?.email) {
                                  window.open(`mailto:${emails[index].email}`, "_blank");
                                }
                              }}
                              title="Enviar E-mail"
                            >
                              <Mail className="h-6 w-6" />
                            </Button>
                            {/* Botão Excluir E-mail (vai para o histórico) */}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-12 w-12 rounded-full bg-white border-2 border-rose-500 text-rose-600 hover:bg-rose-50 shadow-md"
                              onClick={() => excluirEmailParaHistorico(index)}
                              title="Excluir e-mail (vai para o histórico)"
                              data-testid={`email-excluir-${index}`}
                            >
                              <Trash2 className="h-6 w-6" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Histórico de E-mails Excluídos (espelha a coluna da esquerda) */}
                {emailsExcluidos.length > 0 && (
                  <Card className="border-rose-200" data-testid="historico-emails-excluidos">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2 text-rose-700">
                        <Trash2 className="h-4 w-4" />
                        E-mails Excluídos do Histórico ({emailsExcluidos.length})
                      </CardTitle>
                      <CardDescription>
                        Estes e-mails foram removidos da lista ativa do cliente.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {emailsExcluidos.map((ex, i) => (
                        <div key={i} className="flex items-center justify-between border border-rose-100 rounded-md px-3 py-2 bg-rose-50/50">
                          <div className="flex flex-col min-w-0">
                            <span className="font-mono text-sm font-semibold text-rose-700 break-all">{ex.email}</span>
                            <span className="text-xs text-slate-500">
                              {ex.motivo || "—"}
                              {ex.excluido_por ? ` • por ${ex.excluido_por}` : ""}
                              {ex.excluido_em ? ` • ${new Date(ex.excluido_em).toLocaleDateString("pt-BR")}` : ""}
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 shrink-0 ml-2"
                            onClick={() => setEmailsExcluidos((prev) => prev.filter((_, idx) => idx !== i))}
                            title="Restaurar e-mail"
                            data-testid={`restaurar-email-${i}`}
                          >
                            Restaurar
                          </Button>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
                </div>
              </div>

            <Separator className="my-2" />
            {/* Seção: Endereço */}
              <Card>
                <CardHeader>
                  <CardTitle>Endereço</CardTitle>
                  <CardDescription>Endereço residencial do cliente - Digite o CEP para buscar automaticamente</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <Label>CEP *</Label>
                      <div className="flex gap-2">
                        <Input
                          value={endereco.cep}
                          onChange={(e) => {
                            const cep = formatCEP(e.target.value);
                            setEndereco({...endereco, cep});
                            // Busca automática quando CEP completo
                            if (cep.replace(/\D/g, "").length === 8) {
                              buscarCEP(cep);
                            }
                          }}
                          placeholder="00000-000"
                          maxLength={9}
                          data-testid="input-cep"
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="icon"
                          onClick={() => buscarCEP(endereco.cep)}
                          disabled={buscandoCEP}
                          data-testid="btn-buscar-cep"
                        >
                          {buscandoCEP ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                        </Button>
                      </div>
                      {cepError && <p className="text-sm text-red-500 mt-1">{cepError}</p>}
                    </div>
                    <div className="lg:col-span-2">
                      <Label>Logradouro *</Label>
                      <Input
                        value={endereco.logradouro}
                        onChange={(e) => setEndereco({...endereco, logradouro: e.target.value})}
                        placeholder="Rua, Avenida, etc."
                        data-testid="input-logradouro"
                      />
                    </div>
                    <div>
                      <Label>Número *</Label>
                      <Input
                        value={endereco.numero}
                        onChange={(e) => setEndereco({...endereco, numero: e.target.value})}
                        placeholder="Nº"
                        data-testid="input-numero"
                      />
                    </div>
                    <div>
                      <Label>Complemento</Label>
                      <Input
                        value={endereco.complemento}
                        onChange={(e) => setEndereco({...endereco, complemento: e.target.value})}
                        placeholder="Apto, Bloco, etc."
                      />
                    </div>
                    <div>
                      <Label>Bairro *</Label>
                      <Input
                        value={endereco.bairro}
                        onChange={(e) => setEndereco({...endereco, bairro: e.target.value})}
                        placeholder="Bairro"
                        data-testid="input-bairro"
                      />
                    </div>
                    <div>
                      <Label>Estado *</Label>
                      <Select value={endereco.estado} onValueChange={(v) => setEndereco({...endereco, estado: v})}>
                        <SelectTrigger data-testid="select-estado">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {lookups.estados.map((est) => (
                            <SelectItem key={est.sigla} value={est.sigla}>{est.sigla} - {est.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Cidade *</Label>
                      <Input
                        value={endereco.cidade}
                        onChange={(e) => setEndereco({...endereco, cidade: e.target.value})}
                        placeholder="Cidade"
                        data-testid="input-cidade"
                      />
                    </div>
                    <div>
                      <Label>Tipo de Residência</Label>
                      <Select value={endereco.tipo_residencia} onValueChange={(v) => setEndereco({...endereco, tipo_residencia: v})}>
                        <SelectTrigger data-testid="select-tipo-residencia">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {tipoResidenciaOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="lg:col-span-2">
                      <Label>Observações</Label>
                      <Textarea
                        value={endereco.observacoes}
                        onChange={(e) => setEndereco({...endereco, observacoes: e.target.value})}
                        placeholder="Observações sobre o endereço"
                        rows={2}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            <Separator className="my-2" />
            {/* Seção: Emprego */}
              <Card>
                <CardHeader>
                  <CardTitle>Dados do Emprego</CardTitle>
                  <CardDescription>Informações profissionais do cliente</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Instituições - Apenas Visualização */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold">Instituições</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const novaInst = {
                            id: `inst-${Date.now()}`,
                            nome: "",
                            lotacao: "",
                            cargo: "",
                            regimento: "",
                            matricula: "",
                            matricula_instituidor: "",
                            data_admissao: "",
                            dia_pagamento: null,
                            salario: "",
                            tipo_salario: "",
                            editando: true
                          };
                          setInstituicoes([...instituicoes, novaInst]);
                        }}
                        className="gap-2"
                        data-testid="btn-adicionar-instituicao"
                      >
                        <Plus className="h-4 w-4" />
                        Adicionar Instituição
                      </Button>
                    </div>
                    
                    {instituicoes.length === 0 && (
                      <div className="p-6 border-2 border-dashed rounded-lg text-center text-gray-500">
                        <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>Nenhuma instituição vinculada</p>
                        <p className="text-sm">Clique em "Adicionar Instituição" para vincular</p>
                      </div>
                    )}
                    
                    {instituicoes.map((inst, index) => {
                      const ehPensao = inst.regimento === "PENSIONISTA" || /PENS[AÃ]O/i.test(inst.lotacao || "");
                      const updateInstField = (field, value) => {
                        const updated = [...instituicoes];
                        updated[index] = { ...updated[index], [field]: value };
                        setInstituicoes(updated);
                      };
                      
                      const removerInstituicao = () => {
                        const updated = instituicoes.filter((_, i) => i !== index);
                        setInstituicoes(updated);
                      };
                      
                      return (
                      <div key={inst.id} className="p-4 border rounded-lg bg-slate-50 space-y-4">
                        {/* Header da Instituição com botões de ação */}
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <Label className="text-sm font-semibold text-slate-700 mb-1 block">{index + 1}ª Instituição {index === 0 && "*"}</Label>
                            <InstituicaoCombobox
                              value={inst.nome || ""}
                              onChange={(v) => updateInstField('nome', v)}
                              options={lookups.instituicoes || []}
                              testid={`select-instituicao-${index}`}
                            />
                          </div>
                          
                          {/* Botões de Ação */}
                          <div className="flex items-end gap-2 pb-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => updateInstField('editando', !inst.editando)}
                              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              title={inst.editando ? "Fechar edição" : "Editar detalhes"}
                              data-testid={`btn-editar-instituicao-${index}`}
                            >
                              {inst.editando ? <ChevronUp className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
                            </Button>
                            {instituicoes.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={removerInstituicao}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                title="Remover instituição"
                                data-testid={`btn-remover-instituicao-${index}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Campos vinculados à instituição - sempre expandido se editando ou se tem nome */}
                        {inst.nome && (inst.editando !== false) && (
                          <div className="grid gap-4 md:grid-cols-4 pt-2 border-t">
                            <div>
                              <Label>Órgão/Lotação/Espécie</Label>
                              {inst.nome?.startsWith("037") ? (
                                <InstituicaoCombobox
                                  value={inst.lotacao || ""}
                                  onChange={(v) => updateInstField('lotacao', v)}
                                  options={(lookups.todasEspecies || []).map((esp) => `${esp.codigo} - ${esp.nome}`)}
                                  placeholder="Selecione a espécie"
                                  testid={`select-especie-${index}`}
                                />
                              ) : temLotacoes && index === 0 ? (
                                <InstituicaoCombobox
                                  value={inst.lotacao || ""}
                                  onChange={(v) => updateInstField('lotacao', v)}
                                  options={lotacoesDisponiveis}
                                  placeholder="Selecione a lotação"
                                  testid={`select-lotacao-${index}`}
                                />
                              ) : (
                                <Input
                                  value={inst.lotacao || ""}
                                  onChange={(e) => updateInstField('lotacao', e.target.value)}
                                  placeholder="Departamento"
                                />
                              )}
                            </div>
                            <div>
                              <Label>Regimento</Label>
                              <Select value={inst.regimento || ""} onValueChange={(v) => updateInstField('regimento', v)}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                  {regimentoOptions.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className={ehPensao ? "grid grid-cols-2 gap-2" : ""}>
                              <div>
                                <Label>{inst.nome?.startsWith("037") ? "Matrícula/Nº do Benefício" : "Matrícula"}</Label>
                                <Input
                                  value={inst.matricula || ""}
                                  onChange={(e) => updateInstField('matricula', e.target.value)}
                                  placeholder={inst.nome?.startsWith("037") ? "Nº do Benefício" : "Matrícula"}
                                  data-testid={`input-matricula-${index}`}
                                />
                              </div>
                              {ehPensao && (
                                <div>
                                  <Label className="text-xs leading-tight">Matrícula do instituidor</Label>
                                  <Input
                                    value={inst.matricula_instituidor || ""}
                                    onChange={(e) => updateInstField('matricula_instituidor', e.target.value)}
                                    placeholder="Matrícula do instituidor"
                                    data-testid={`input-matricula-instituidor-${index}`}
                                  />
                                </div>
                              )}
                            </div>
                            {inst.nome?.startsWith("037") && (
                              <div>
                                <Label>UF do Benefício</Label>
                                <Select value={inst.uf_beneficio || ""} onValueChange={(v) => updateInstField('uf_beneficio', v)}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="UF" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {lookups.estados?.map((est) => (
                                      <SelectItem key={est.sigla} value={est.sigla}>{est.sigla}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            <div>
                              <Label>Data de Admissão/DDB</Label>
                              <Input
                                type="date"
                                value={inst.data_admissao || ""}
                                onChange={(e) => updateInstField('data_admissao', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Tempo de Benefício</Label>
                              <div className="h-10 px-3 py-2 rounded-md border border-input bg-slate-50 text-sm flex items-center font-medium text-slate-700">
                                {(() => {
                                  if (!inst.data_admissao) return "—";
                                  try {
                                    const dataAdmissao = new Date(inst.data_admissao);
                                    if (isNaN(dataAdmissao.getTime())) return "—";
                                    
                                    const hoje = new Date();
                                    let anos = hoje.getFullYear() - dataAdmissao.getFullYear();
                                    let meses = hoje.getMonth() - dataAdmissao.getMonth();
                                    
                                    if (meses < 0) {
                                      anos--;
                                      meses += 12;
                                    }
                                    
                                    if (hoje.getDate() < dataAdmissao.getDate()) {
                                      meses--;
                                      if (meses < 0) {
                                        anos--;
                                        meses += 12;
                                      }
                                    }
                                    
                                    if (anos < 0) return "—";
                                    
                                    if (anos === 0 && meses === 0) return "Menos de 1 mês";
                                    if (anos === 0) return `${meses} ${meses === 1 ? 'Mês' : 'Meses'}`;
                                    if (meses === 0) return `${anos} ${anos === 1 ? 'Ano' : 'Anos'}`;
                                    return `${anos} ${anos === 1 ? 'Ano' : 'Anos'} e ${meses} ${meses === 1 ? 'Mês' : 'Meses'}`;
                                  } catch {
                                    return "—";
                                  }
                                })()}
                              </div>
                            </div>
                            <div>
                              <Label>Dia do Pagamento</Label>
                              <Select value={inst.dia_pagamento?.toString() || ""} onValueChange={(v) => updateInstField('dia_pagamento', parseInt(v))}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Dia" />
                                </SelectTrigger>
                                <SelectContent>
                                  {Array.from({length: 31}, (_, i) => i + 1).map((dia) => (
                                    <SelectItem key={dia} value={dia.toString()}>{dia.toString().padStart(2, '0')}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Salário (R$)</Label>
                              <Input
                                value={inst.salario || ""}
                                onChange={(e) => updateInstField('salario', e.target.value)}
                                placeholder="0,00"
                              />
                            </div>
                            <div>
                              <Label>Tipo de Salário</Label>
                              <Select value={inst.tipo_salario || ""} onValueChange={(v) => updateInstField('tipo_salario', v)}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Tipo" />
                                </SelectTrigger>
                                <SelectContent>
                                  {tipoSalarioOptions.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {inst.nome?.startsWith("037") && (
                              <div>
                                <Label>Situação</Label>
                                <Select value={inst.situacao || ""} onValueChange={(v) => updateInstField('situacao', v)}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Situação" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="ATIVO">Ativo</SelectItem>
                                    <SelectItem value="INATIVO">Inativo</SelectItem>
                                    <SelectItem value="SUSPENSO">Suspenso</SelectItem>
                                    <SelectItem value="CESSADO">Cessado</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            {!inst.nome?.startsWith("037") && (
                              <>
                                <div>
                                  <Label>Cargo</Label>
                                  <Input
                                    value={inst.cargo || ""}
                                    onChange={(e) => updateInstField('cargo', e.target.value)}
                                    placeholder="Cargo"
                                  />
                                </div>
                                <div>
                                  <Label>Situação Funcional</Label>
                                  <Select value={inst.situacao_funcional || ""} onValueChange={(v) => updateInstField('situacao_funcional', v)}>
                                    <SelectTrigger data-testid="situacao-funcional-select">
                                      <SelectValue placeholder="Selecione" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-60">
                                      {situacaoFuncionalOptions.map((opt) => (
                                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </>
                            )}
                            {inst.regimento === "CLT" && (
                              <>
                                <div>
                                  <Label>CNPJ</Label>
                                  <Input
                                    value={inst.cnpj || ""}
                                    onChange={(e) => updateInstField('cnpj', e.target.value)}
                                    placeholder="00.000.000/0000-00"
                                    data-testid={`input-cnpj-${index}`}
                                  />
                                </div>
                                <div>
                                  <Label>CNAE Principal</Label>
                                  <Input
                                    value={inst.cnae || ""}
                                    onChange={(e) => updateInstField('cnae', e.target.value)}
                                    placeholder="CNAE Principal"
                                    data-testid={`input-cnae-${index}`}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        {/* Flags INSS - apenas para instituição INSS (037) */}
                        {inst.nome && inst.nome.startsWith("037") && (inst.editando !== false) && (
                          <div className="grid gap-2 md:grid-cols-3 lg:grid-cols-5 pt-3 mt-3 border-t">
                            <div className="flex items-center gap-2 p-2 rounded bg-slate-50">
                              <Switch
                                checked={!inst.possui_procurador}
                                onCheckedChange={(v) => updateInstField('possui_procurador', !v)}
                              />
                              <Label className="text-xs">Não possui procurador</Label>
                            </div>
                            <div className="flex items-center gap-2 p-2 rounded bg-slate-50">
                              <Switch
                                checked={!inst.possui_representante_legal}
                                onCheckedChange={(v) => updateInstField('possui_representante_legal', !v)}
                              />
                              <Label className="text-xs">Não possui rep. legal</Label>
                            </div>
                            <div className="flex items-center gap-2 p-2 rounded bg-slate-50">
                              <Switch
                                checked={!inst.pensao_alimenticia}
                                onCheckedChange={(v) => updateInstField('pensao_alimenticia', !v)}
                              />
                              <Label className="text-xs">Não é pensão alim.</Label>
                            </div>
                            <div className="flex items-center gap-2 p-2 rounded bg-red-50">
                              <Switch
                                checked={inst.bloqueado_emprestimo}
                                onCheckedChange={(v) => updateInstField('bloqueado_emprestimo', v)}
                              />
                              <Label className="text-xs text-red-700">Bloqueado</Label>
                            </div>
                            <div className="flex items-center gap-2 p-2 rounded bg-green-50">
                              <Switch
                                checked={inst.elegivel_emprestimo !== false}
                                onCheckedChange={(v) => updateInstField('elegivel_emprestimo', v)}
                              />
                              <Label className="text-xs text-green-700">Elegível</Label>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                    })}
                  </div>
                </CardContent>
              </Card>
            <Separator className="my-2" />
            {/* Seção: Dados Bancários */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Dados para Liberação do Crédito</CardTitle>
                    <CardDescription>Contas bancárias para recebimento</CardDescription>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addContaBancaria} className="gap-2" data-testid="add-conta-btn">
                    <Plus className="h-4 w-4" />
                    Adicionar Conta
                  </Button>
                </CardHeader>
                <CardContent>
                  {contasBancarias.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Nenhuma conta bancária cadastrada</p>
                      <Button type="button" variant="link" onClick={addContaBancaria}>Adicionar primeira conta</Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {contasBancarias.map((conta, index) => (
                        <div key={conta.id} className="p-4 border rounded-lg">
                          {/* Linha 1: Toggle Principal + Excluir */}
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <Switch
                                checked={conta.principal}
                                onCheckedChange={(v) => {
                                  // Desmarcar outras contas como principal
                                  const updated = contasBancarias.map((c, i) => ({
                                    ...c,
                                    principal: i === index ? v : false
                                  }));
                                  setContasBancarias(updated);
                                }}
                              />
                              <Label className="font-semibold">Conta Principal</Label>
                            </div>
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeContaBancaria(index)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                              <Trash2 className="h-5 w-5" />
                            </Button>
                          </div>
                          
                          {/* Linha 2: Campos em uma linha - Meio de Recebimento, Tipo Conta, Banco, Agência, Conta, Variação */}
                          <div className="grid gap-3 grid-cols-6">
                            <div>
                              <Label className="text-sm text-slate-600">Meio de Recebimento</Label>
                              <Select value={conta.meio_recebimento} onValueChange={(v) => updateContaBancaria(index, "meio_recebimento", v)}>
                                <SelectTrigger className="h-11">
                                  <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                  {meioRecebimentoOptions.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm text-slate-600">Tipo Conta</Label>
                              <Select value={conta.tipo_conta} onValueChange={(v) => updateContaBancaria(index, "tipo_conta", v)}>
                                <SelectTrigger className="h-11">
                                  <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                  {tipoContaOptions.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm text-slate-600">Banco</Label>
                              <Select value={conta.banco} onValueChange={(v) => updateContaBancaria(index, "banco", v)}>
                                <SelectTrigger className="h-11" data-testid={`select-banco-${index}`}>
                                  <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                  {lookups.bancos.map((banco) => (
                                    <SelectItem key={banco.value} value={banco.label}>{banco.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm text-slate-600">Agência</Label>
                              <Input
                                value={conta.agencia}
                                onChange={(e) => updateContaBancaria(index, "agencia", e.target.value)}
                                placeholder="0000"
                                className="h-11 font-mono"
                                data-testid={`input-agencia-${index}`}
                              />
                            </div>
                            <div>
                              <Label className="text-sm text-slate-600">Conta</Label>
                              <Input
                                value={conta.conta}
                                onChange={(e) => updateContaBancaria(index, "conta", e.target.value)}
                                placeholder="00000-0"
                                className="h-11 font-mono"
                                data-testid={`input-conta-${index}`}
                              />
                            </div>
                            <div>
                              <Label className="text-sm text-slate-600">Variação</Label>
                              <Input
                                value={conta.variacao}
                                onChange={(e) => updateContaBancaria(index, "variacao", e.target.value)}
                                placeholder="0"
                                className="h-11 font-mono"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            <Separator className="my-6" />
            {/* Seção: Ações Operacionais & Restrições (movida da antiga aba Operacional) */}
            <div className="rounded-lg border-2 border-slate-200 bg-slate-50/60 p-4">
              <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2" data-testid="secao-operacional-titulo">
                <Settings className="h-5 w-5 text-slate-600" />
                Operacional &amp; Restrições
              </h2>
              <Card>
                <CardHeader>
                  <CardTitle>Ações Operacionais</CardTitle>
                  <CardDescription>Status e configurações do cliente</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Status do Cliente</Label>
                      <Select value={acoesOperacionais.status} onValueChange={(v) => setAcoesOperacionais({...acoesOperacionais, status: v})}>
                        <SelectTrigger data-testid="select-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {acoesOperacionais.status !== "ATIVO" && (
                      <div>
                        <Label>Motivo da Inativação</Label>
                        <Input
                          value={acoesOperacionais.motivo_inativo}
                          onChange={(e) => setAcoesOperacionais({...acoesOperacionais, motivo_inativo: e.target.value})}
                          placeholder="Motivo"
                        />
                      </div>
                    )}
                    <div className="md:col-span-2">
                      <Label>Observações</Label>
                      <Textarea
                        value={acoesOperacionais.observacoes}
                        onChange={(e) => setAcoesOperacionais({...acoesOperacionais, observacoes: e.target.value})}
                        placeholder="Observações gerais sobre o cliente"
                        rows={4}
                        data-testid="textarea-observacoes"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Card de Restrições do Cliente */}
              <Card className="mt-4 border-red-200 bg-red-50/30">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Ban className="h-5 w-5 text-red-600" />
                    <CardTitle className="text-red-700">Restrições do Cliente</CardTitle>
                  </div>
                  <CardDescription className="text-red-600/80">
                    Configure bancos com restrição interna e motivos de restrição geral do cliente
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  
                  {/* SEÇÃO 1: Bancos com Restrição Interna */}
                  <div className="border border-red-200 rounded-lg p-4 bg-white/50">
                    <Label className="text-red-700 font-semibold text-sm block mb-3">
                      Bancos com Restrição Interna
                    </Label>
                    <p className="text-red-500 text-xs mb-3">
                      Bancos onde o cliente está impedido de operar
                    </p>
                    
                    {/* Formulário para adicionar banco */}
                    <div className="flex items-end gap-3 mb-3">
                      <div className="flex-1">
                        <Select 
                          value={novoBancoRestricao.banco_id} 
                          onValueChange={(v) => {
                            const bancoSelecionado = lookups.bancos.find(b => b.value === v);
                            setNovoBancoRestricao({
                              banco_id: v,
                              banco_nome: bancoSelecionado?.label || ""
                            });
                          }}
                        >
                          <SelectTrigger className="border-red-200" data-testid="select-banco-restricao">
                            <SelectValue placeholder="Selecione o banco" />
                          </SelectTrigger>
                          <SelectContent>
                            {lookups.bancos.map((banco) => (
                              <SelectItem key={banco.value} value={banco.value}>{banco.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button 
                        type="button"
                        onClick={adicionarBancoRestricao}
                        disabled={!novoBancoRestricao.banco_id}
                        className="bg-red-600 hover:bg-red-700 h-10"
                        data-testid="btn-adicionar-banco-restricao"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Lista de bancos com restrição */}
                    {acoesOperacionais.bancosRestricaoInterna && acoesOperacionais.bancosRestricaoInterna.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {acoesOperacionais.bancosRestricaoInterna.map((item, index) => (
                          <div 
                            key={index} 
                            className="flex items-center gap-2 bg-red-100 border border-red-300 rounded-lg px-3 py-2"
                            data-testid={`banco-restricao-${item.banco_id}`}
                          >
                            <span className="font-semibold text-red-800 text-sm">{item.banco_nome}</span>
                            <button
                              type="button"
                              onClick={() => removerBancoRestricao(item.banco_id)}
                              className="text-red-600 hover:text-red-800 hover:bg-red-200 rounded-full p-1"
                              data-testid={`btn-remover-banco-restricao-${item.banco_id}`}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-2 text-red-400 text-xs">
                        Nenhum banco com restrição cadastrado
                      </div>
                    )}
                  </div>

                  {/* SEÇÃO 2: Motivos de Restrição Geral */}
                  <div className="border border-orange-200 rounded-lg p-4 bg-orange-50/30">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <Label className="text-orange-700 font-semibold text-sm block">
                          Motivos de Restrição Geral
                        </Label>
                        <p className="text-orange-500 text-xs">
                          Restrições gerais que se aplicam ao cliente
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setGerenciarMotivosOpen(true)}
                        className="border-orange-300 text-orange-700 hover:bg-orange-100"
                        data-testid="btn-gerenciar-motivos"
                      >
                        <Settings className="h-4 w-4 mr-1" />
                        Gerenciar Motivos
                      </Button>
                    </div>
                    
                    {/* Formulário para adicionar motivo */}
                    <div className="flex items-end gap-3 mb-3">
                      <div className="flex-1">
                        <Select 
                          value={novoMotivoRestricao} 
                          onValueChange={(v) => setNovoMotivoRestricao(v)}
                        >
                          <SelectTrigger className="border-orange-200" data-testid="select-motivo-restricao">
                            <SelectValue placeholder="Selecione o motivo" />
                          </SelectTrigger>
                          <SelectContent>
                            {motivosRestricao.map((motivo) => (
                              <SelectItem key={motivo.value} value={motivo.value}>{motivo.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button 
                        type="button"
                        onClick={adicionarMotivoRestricao}
                        disabled={!novoMotivoRestricao}
                        className="bg-orange-600 hover:bg-orange-700 h-10"
                        data-testid="btn-adicionar-motivo-restricao"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Lista de motivos de restrição */}
                    {acoesOperacionais.motivosRestricaoGeral && acoesOperacionais.motivosRestricaoGeral.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {acoesOperacionais.motivosRestricaoGeral.map((item, index) => {
                          const motivoLabel = motivosRestricao.find(m => m.value === item.motivo)?.label || item.motivo;
                          return (
                            <div 
                              key={index} 
                              className="flex items-center gap-2 bg-orange-100 border border-orange-300 rounded-lg px-3 py-2"
                              data-testid={`motivo-restricao-${item.motivo}`}
                            >
                              <span className="font-semibold text-orange-800 text-sm">{motivoLabel}</span>
                              <button
                                type="button"
                                onClick={() => removerMotivoRestricao(item.motivo)}
                                className="text-orange-600 hover:text-orange-800 hover:bg-orange-200 rounded-full p-1"
                                data-testid={`btn-remover-motivo-restricao-${item.motivo}`}
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-2 text-orange-400 text-xs">
                        Nenhum motivo de restrição cadastrado
                      </div>
                    )}
                  </div>

                </CardContent>
              </Card>

              {/* Modal de Gerenciamento de Motivos de Restrição */}
              <Dialog open={gerenciarMotivosOpen} onOpenChange={setGerenciarMotivosOpen}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-orange-700">
                      <Settings className="h-5 w-5" />
                      Gerenciar Motivos de Restrição
                    </DialogTitle>
                    <DialogDescription>
                      Adicione, edite ou exclua motivos de restrição disponíveis no sistema
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-4">
                    {/* Formulário para adicionar/editar motivo */}
                    <div className="border border-orange-200 rounded-lg p-4 bg-orange-50/30">
                      <h4 className="font-semibold text-orange-700 text-sm mb-3">
                        {motivoEditando ? "Editar Motivo" : "Novo Motivo"}
                      </h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs text-orange-600">Código</Label>
                          <Input
                            value={novoMotivo.codigo}
                            onChange={(e) => setNovoMotivo({...novoMotivo, codigo: e.target.value})}
                            placeholder="Ex: 07"
                            className="border-orange-200"
                            disabled={motivoEditando !== null}
                            data-testid="input-novo-motivo-codigo"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs text-orange-600">Nome do Motivo</Label>
                          <Input
                            value={novoMotivo.nome}
                            onChange={(e) => setNovoMotivo({...novoMotivo, nome: e.target.value})}
                            placeholder="Ex: INADIMPLENTE"
                            className="border-orange-200"
                            data-testid="input-novo-motivo-nome"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button
                          type="button"
                          onClick={handleSalvarMotivo}
                          disabled={salvandoMotivo || !novoMotivo.codigo || !novoMotivo.nome}
                          className="bg-orange-600 hover:bg-orange-700"
                          data-testid="btn-salvar-motivo"
                        >
                          {salvandoMotivo ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                          ) : (
                            <Save className="h-4 w-4 mr-1" />
                          )}
                          {motivoEditando ? "Atualizar" : "Adicionar"}
                        </Button>
                        {motivoEditando && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancelarEdicao}
                            className="border-orange-300"
                          >
                            Cancelar
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Lista de motivos existentes */}
                    <div className="border rounded-lg">
                      <div className="bg-slate-50 px-4 py-2 border-b">
                        <h4 className="font-semibold text-slate-700 text-sm">
                          Motivos Cadastrados ({motivosRestricao.length})
                        </h4>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {loadingMotivos ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
                          </div>
                        ) : motivosRestricao.length === 0 ? (
                          <div className="text-center py-8 text-slate-400">
                            Nenhum motivo cadastrado
                          </div>
                        ) : (
                          <div className="divide-y">
                            {motivosRestricao.map((motivo) => (
                              <div 
                                key={motivo.codigo} 
                                className="flex items-center justify-between px-4 py-3 hover:bg-slate-50"
                              >
                                <div>
                                  <span className="font-mono text-orange-600 text-sm mr-2">
                                    {motivo.codigo}
                                  </span>
                                  <span className="text-slate-700">{motivo.nome}</span>
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditarMotivo(motivo)}
                                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                    data-testid={`btn-editar-motivo-${motivo.codigo}`}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleExcluirMotivo(motivo.codigo)}
                                    className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                    data-testid={`btn-excluir-motivo-${motivo.codigo}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setGerenciarMotivosOpen(false);
                        handleCancelarEdicao();
                      }}
                    >
                      Fechar
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            </TabsContent>

            {/* Tab: Documentos */}
            <TabsContent value="documentos">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Documentos Digitalizados</CardTitle>
                    <CardDescription>Documentos de identificação e outros arquivos do cliente</CardDescription>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Área de Upload */}
                  <div className="mb-6 p-4 border-2 border-dashed rounded-lg bg-slate-50">
                    <div className="flex flex-wrap gap-3 justify-center">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => handleUploadDocumentoComDescricao("RG")}
                        disabled={uploadingDoc || !isEditing}
                        className="gap-2"
                      >
                        <Upload className="h-4 w-4" />
                        Upload RG
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => handleUploadDocumentoComDescricao("CNH")}
                        disabled={uploadingDoc || !isEditing}
                        className="gap-2"
                      >
                        <Upload className="h-4 w-4" />
                        Upload CNH
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => handleUploadDocumentoComDescricao("SELFIE")}
                        disabled={uploadingDoc || !isEditing}
                        className="gap-2"
                      >
                        <Camera className="h-4 w-4" />
                        Upload Selfie
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => handleUploadDocumentoComDescricao("COMPROVANTE_RESIDENCIA")}
                        disabled={uploadingDoc || !isEditing}
                        className="gap-2"
                      >
                        <Upload className="h-4 w-4" />
                        Comprovante Residência
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => handleUploadDocumentoComDescricao("CONTRA_CHEQUE")}
                        disabled={uploadingDoc || !isEditing}
                        className="gap-2"
                      >
                        <Upload className="h-4 w-4" />
                        Contra Cheque
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => handleUploadDocumentoComDescricao("EXTRATO")}
                        disabled={uploadingDoc || !isEditing}
                        className="gap-2"
                      >
                        <FileText className="h-4 w-4" />
                        Extrato Consignação SIAPE
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => handleUploadDocumentoComDescricao("EXTRATO_INSS")}
                        disabled={uploadingDoc || !isEditing}
                        className="gap-2"
                      >
                        <FileText className="h-4 w-4" />
                        Extrato Consignação INSS
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => handleUploadDocumentoComDescricao("OUTRO")}
                        disabled={uploadingDoc || !isEditing}
                        className="gap-2"
                      >
                        <FileUp className="h-4 w-4" />
                        Outro Documento
                      </Button>
                    </div>
                    {!isEditing && (
                      <p className="text-sm text-amber-600 text-center mt-3">
                        Salve o cliente primeiro para poder enviar documentos
                      </p>
                    )}
                    {uploadingDoc && (
                      <div className="flex items-center justify-center gap-2 mt-3 text-blue-600">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Enviando documento...</span>
                      </div>
                    )}
                  </div>

                  {/* Tabela de Documentos */}
                  {documentos.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileImage className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="font-medium">Nenhum documento cadastrado</p>
                      <p className="text-sm">Faça upload de documentos usando os botões acima</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-green-700">
                            <TableHead className="text-white font-bold">DESCRIÇÃO</TableHead>
                            <TableHead className="text-white font-bold">TAMANHO</TableHead>
                            <TableHead className="text-white font-bold">OPER. DE CADASTRO</TableHead>
                            <TableHead className="text-white font-bold">DATA DE CADASTRO</TableHead>
                            <TableHead className="text-white font-bold text-center">STATUS</TableHead>
                            <TableHead className="text-white font-bold text-center">VER</TableHead>
                            <TableHead className="text-white font-bold text-center">EXCLUIR</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {documentos.map((doc, index) => (
                            <TableRow key={doc.id} className={index % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                              <TableCell className="font-medium">{doc.descricao || doc.tipo}</TableCell>
                              <TableCell>{formatFileSize(doc.tamanho)}</TableCell>
                              <TableCell>{doc.operador_cadastro || "Sistema"}</TableCell>
                              <TableCell>{formatDataDocumento(doc.data_cadastro)}</TableCell>
                              <TableCell className="text-center">
                                {doc.dados_atualizados ? (
                                  <div className="flex items-center justify-center gap-1">
                                    <CheckCircle className="h-5 w-5 text-green-600" title="Dados atualizados" />
                                    {doc.contratos_importados > 0 && (
                                      <span className="text-xs text-green-600 font-medium">
                                        +{doc.contratos_importados}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleVisualizarDocumento(doc)}
                                  className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Eye className="h-5 w-5" />
                                </Button>
                              </TableCell>
                              <TableCell className="text-center">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleExcluirDocumento(doc.id)}
                                  className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-5 w-5" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Dialog para visualizar documento */}
              <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
                <DialogContent className="!max-w-[90vw] w-[90vw] h-[90vh] max-h-[90vh] flex flex-col">
                  <DialogHeader>
                    <DialogTitle>{previewDoc?.descricao || "Documento"}</DialogTitle>
                    <DialogDescription>
                      {previewDoc?.nome_arquivo} - {formatFileSize(previewDoc?.tamanho)}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex-1 min-h-0 p-2 bg-slate-100 rounded-lg overflow-auto">
                    {previewDoc?.mime_type?.startsWith('image/') ? (
                      <img 
                        src={`data:${previewDoc.mime_type};base64,${previewDoc.conteudo_base64}`}
                        alt={previewDoc.descricao}
                        className="w-full h-auto object-contain"
                      />
                    ) : previewDoc?.mime_type === 'application/pdf' ? (
                      <iframe
                        src={`data:${previewDoc.mime_type};base64,${previewDoc.conteudo_base64}#toolbar=1&navpanes=0&view=FitH`}
                        className="w-full h-full min-h-[70vh]"
                        title={previewDoc.descricao}
                      />
                    ) : (
                      <div className="text-center py-8">
                        <FileText className="h-16 w-16 mx-auto mb-4 text-slate-400" />
                        <p>Visualização não disponível para este tipo de arquivo</p>
                        <Button
                          variant="outline"
                          className="mt-4 gap-2"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `data:${previewDoc.mime_type};base64,${previewDoc.conteudo_base64}`;
                            link.download = previewDoc.nome_arquivo;
                            link.click();
                          }}
                        >
                          <Download className="h-4 w-4" />
                          Baixar Arquivo
                        </Button>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = `data:${previewDoc?.mime_type};base64,${previewDoc?.conteudo_base64}`;
                        link.download = previewDoc?.nome_arquivo;
                        link.click();
                      }}
                      className="gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Baixar
                    </Button>
                    <Button onClick={() => setPreviewDoc(null)}>Fechar</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </TabsContent>


            {/* Tab: Auditoria WhatsApp */}
            <TabsContent value="auditoria">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-green-600" />
                      Auditoria de Conversas WhatsApp
                    </CardTitle>
                    <CardDescription>Registro de todas as conversas salvas automaticamente ao finalizar atendimentos</CardDescription>
                  </div>
                  {auditorias.length > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="flex bg-gray-100 rounded-lg p-0.5">
                        <Button type="button" size="sm" variant={auditoriaMode === "separado" ? "default" : "ghost"}
                          className={`text-xs h-7 ${auditoriaMode === "separado" ? "bg-green-600 hover:bg-green-700" : ""}`}
                          onClick={() => setAuditoriaMode("separado")} data-testid="btn-auditoria-separado">
                          Separado
                        </Button>
                        <Button type="button" size="sm" variant={auditoriaMode === "unificado" ? "default" : "ghost"}
                          className={`text-xs h-7 ${auditoriaMode === "unificado" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                          onClick={() => setAuditoriaMode("unificado")} data-testid="btn-auditoria-unificado">
                          Unificado
                        </Button>
                      </div>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {auditoriaMode === "unificado" && auditorias.length > 0 ? (
                    <div className="text-center py-8">
                      <ShieldCheck className="h-16 w-16 mx-auto mb-4 text-blue-500 opacity-70" />
                      <h3 className="text-lg font-bold text-gray-700 mb-1">PDF Unificado</h3>
                      <p className="text-sm text-gray-500 mb-4">
                        Todas as {auditorias.length} conversas em um unico documento PDF
                      </p>
                      <Button type="button" className="bg-blue-600 hover:bg-blue-700 gap-2"
                        onClick={() => window.open(`${API}/wpp/auditoria/${id}/unificado`, "_blank")}
                        data-testid="btn-download-unificado">
                        <Download className="h-4 w-4" /> Baixar PDF Unificado
                      </Button>
                    </div>
                  ) : auditorias.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <ShieldCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="font-medium">Nenhuma conversa registrada</p>
                      <p className="text-sm">As conversas serao salvas automaticamente ao finalizar atendimentos WhatsApp</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-green-700">
                            <TableHead className="text-white font-bold">#</TableHead>
                            <TableHead className="text-white font-bold">DESCRICAO</TableHead>
                            <TableHead className="text-white font-bold">TAMANHO</TableHead>
                            <TableHead className="text-white font-bold">DATA</TableHead>
                            <TableHead className="text-white font-bold text-center">VER</TableHead>
                            <TableHead className="text-white font-bold text-center">BAIXAR</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {auditorias.map((doc, index) => (
                            <TableRow key={doc.id} className={index % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                              <TableCell className="font-mono text-xs text-gray-400">{index + 1}</TableCell>
                              <TableCell className="font-medium text-sm">{doc.descricao || "Conversa WhatsApp"}</TableCell>
                              <TableCell className="text-sm">{formatFileSize(doc.tamanho)}</TableCell>
                              <TableCell className="text-sm">{formatDataDocumento(doc.data_cadastro)}</TableCell>
                              <TableCell className="text-center">
                                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  onClick={async () => {
                                    try {
                                      const r = await axios.get(`${API}/clientes/${id}/documentos`);
                                      const fullDoc = (r.data.documentos || []).find(d => d.id === doc.id);
                                      if (fullDoc) setPreviewDoc(fullDoc);
                                      else toast.error("Documento nao encontrado");
                                    } catch { toast.error("Erro ao carregar documento"); }
                                  }} data-testid={`btn-ver-auditoria-${index}`}>
                                  <Eye className="h-5 w-5" />
                                </Button>
                              </TableCell>
                              <TableCell className="text-center">
                                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => window.open(`${API}/wpp/auditoria/${id}/doc/${doc.id}`, "_blank")}
                                  data-testid={`btn-baixar-auditoria-${index}`}>
                                  <Download className="h-5 w-5" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </form>
      </Form>
      {/* Modal SMS Individual */}
      <Dialog open={showSmsModal} onOpenChange={setShowSmsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-purple-500" />
              Enviar SMS
            </DialogTitle>
            <DialogDescription>
              Enviar mensagem SMS para <strong>{smsNumero}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Mensagem</Label>
              <Textarea
                value={smsTexto}
                onChange={(e) => setSmsTexto(e.target.value)}
                placeholder="Digite sua mensagem..."
                rows={4}
                maxLength={160}
                className="mt-1"
                data-testid="sms-individual-texto"
              />
              <p className="text-xs text-muted-foreground mt-1 text-right">{smsTexto.length}/160</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSmsModal(false)}>Cancelar</Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700 gap-2" 
              onClick={enviarSmsIndividual} 
              disabled={smsSending || !smsTexto.trim()}
              data-testid="sms-individual-enviar"
            >
              {smsSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {smsSending ? "Enviando..." : "Enviar SMS"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmar Exclusão de Número */}
      <Dialog open={confirmExclusao.open} onOpenChange={(open) => !open && setConfirmExclusao({ open: false, index: null, numero: "", motivo: "" })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <Trash2 className="h-5 w-5" />
              Excluir número do cliente
            </DialogTitle>
            <DialogDescription>
              O número <strong className="font-mono">{confirmExclusao.numero}</strong> será desvinculado do CPF e enviado ao histórico.
              <span className="block mt-1 text-rose-600 font-medium">Ele NÃO será readicionado em novas importações.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motivo (opcional)</Label>
            <Input
              value={confirmExclusao.motivo}
              onChange={(e) => setConfirmExclusao((prev) => ({ ...prev, motivo: e.target.value }))}
              placeholder="Ex: número errado, cliente pediu remoção..."
              data-testid="exclusao-motivo"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmExclusao({ open: false, index: null, numero: "", motivo: "" })} data-testid="exclusao-cancelar">
              Cancelar
            </Button>
            <Button className="bg-rose-600 hover:bg-rose-700 gap-2" onClick={confirmarExclusaoTelefone} data-testid="exclusao-confirmar">
              <Trash2 className="h-4 w-4" />
              Excluir e enviar ao histórico
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
